import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchFromPaymentsAPI, FRONTEND_BASE_URL } from '../services/common.js';
import StaffAddCardForm from './StaffAddCardForm.js';
import VirtualTerminalForm from './VirtualTerminalForm.js';
import CollectPaymentModal from './CollectPaymentModal.js';
import LedgerTab from './LedgerTab.js';
import Icon from './Icon';
import KpiTile from './KpiTile';
import Pager from './Pager';
import InsuranceAllocations, { rowsTotal } from './InsuranceAllocations';
import RowMenu from './RowMenu';
import { PlanDetailDrawer } from './PaymentPlansTab';
import {
  listDevices,
  sendInvoiceToDevice,
  saveCardOnDevice,
  cancelDevicePresentation,
  sendInvoiceEmail,
  sendInvoiceReceipt,
  logInvoiceActivity,
  listPatientPaymentMethods,
  chargeInvoiceWithSavedCard,
  simulateDeviceInteraction,
  SIMULATE_DEVICE_PRESETS,
  isSandboxRainforestId,
  createInvoice,
  cancelInvoice,
  resolveInvoiceAttention,
  resolveInvoiceAttentionBulk,
  getPatientLedger,
  getPaymentPlan,
} from '../services/paymentsService.js';

// Helper function to get status styling for invoices
// Updated for Rainforest API v2024-10-16 statuses
const getStatusStyle = (status) => {
  const normalizedStatus = (status || 'pending').toLowerCase();
  
  // Define status categories and their colors
  const statusStyles = {
    // Pending/Waiting states - Yellow
    pending: { bg: '#fdf3e3', color: '#a8520c', label: 'Pending' },
    
    // Success states - Green
    paid: { bg: '#e7f4ec', color: '#1d6b40', label: 'Paid' },

    // Voided / replaced states - Muted gray. `superseded` means a newer
    // invoice was created for this patient and this one is no longer
    // collectible; we keep the row for audit purposes only.
    superseded: { bg: '#eef0ee', color: '#3c4540', label: 'Superseded' },
    // `cancelled` is the operator-driven invoice-level void (the
    // staff hit "Cancel Invoice" in the Timeline modal). Distinct
    // from Rainforest's payin-level ``canceled`` event below, which
    // can fire on a still-pending invoice when a single tap attempt
    // is aborted.
    cancelled: { bg: '#eef0ee', color: '#3c4540', label: 'Cancelled' },
    
    // Processing states - Blue
    authorized: { bg: '#e8f0f7', color: '#1f4e79', label: 'Authorized' },
    processing: { bg: '#e8f0f7', color: '#1f4e79', label: 'Processing' },
    
    // Warning states - Orange
    returned: { bg: '#fdeee4', color: '#9a3412', label: 'Returned' },
    under_review: { bg: '#fdeee4', color: '#9a3412', label: 'Under Review' },
    canceled: { bg: '#eef0ee', color: '#3c4540', label: 'Canceled' },
    
    // Error states - Red
    failed: { bg: '#fdecea', color: '#912018', label: 'Failed' },
    
    // Chargeback states - Various
    chargeback_inquiry_action_required: { bg: '#fdf3e3', color: '#a8520c', label: 'Inquiry Required' },
    chargeback_inquiry_processing: { bg: '#e8f0f7', color: '#1f4e79', label: 'Inquiry Processing' },
    chargeback_inquiry_responded: { bg: '#e8f0f7', color: '#1f4e79', label: 'Inquiry Responded' },
    chargeback_dispute_action_required: { bg: '#fdecea', color: '#912018', label: 'Dispute Required' },
    chargeback_processing: { bg: '#e8f0f7', color: '#1f4e79', label: 'Chargeback Processing' },
    chargeback_prearbitration: { bg: '#fdeee4', color: '#9a3412', label: 'Pre-Arbitration' },
    chargeback_arbitration: { bg: '#fdeee4', color: '#9a3412', label: 'Arbitration' },
    chargeback_provisional_win: { bg: '#e7f4ec', color: '#1d6b40', label: 'Provisional Win' },
    chargeback_lost: { bg: '#fdecea', color: '#912018', label: 'Chargeback Lost' },
    chargeback_won: { bg: '#e7f4ec', color: '#1d6b40', label: 'Chargeback Won' },
  };
  
  // Return matching style or default to pending style
  return statusStyles[normalizedStatus] || { 
    bg: '#eef0ee', 
    color: '#3c4540', 
    label: normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1).replace(/_/g, ' ')
  };
};

// Helper function to get PMS reconciliation status styling.
//
// ``ledgerPost`` (the invoice's ledger-writeback stamp) wins when present: if
// Newton itself posted the payment to the PMS ledger, we say so immediately —
// even before the payment settles, since the autopost fires at
// ``payin.processing``. That's more informative than a generic "Matched"
// (which only means we later found the row) and tells staff NOT to post it
// manually.
//
// The column also shows for in-flight (``processing``) payments, not just
// paid ones — the office shouldn't have to wait for settlement to see whether
// a payment made it into the PMS. Returns null (no chip) for invoices with no
// payment in play (plain unpaid ``pending`` / ``cancelled`` / ``superseded``).
export const getReconciliationStatusStyle = (reconciliationStatus, invoiceStatus, ledgerPost) => {
  // Newton posted this payment to the PMS, then it was voided, failed,
  // returned or refunded: the ledger entry must be reversed by hand.
  if (ledgerPost && ledgerPost.reversal && ledgerPost.reversal.status === 'needed') {
    return {
      bg: '#fdecea', color: '#912018', label: 'Reverse in PMS', icon: '↺',
      title: `${ledgerPost.reversal.reason || 'Payment reversed after it was posted'} — reverse it in the PMS`,
    };
  }
  // Newton posted this payment to the PMS ledger itself. The chip stays
  // "Checking PMS…" until that specific row is in our ledger cache
  // (``ledgerPost.rowVisible``, set when the patient Kolla sync finds the
  // Newton reference) or the reconciler has matched it. The statement
  // watch also flips it locally once the row is in the ledger response.
  // ``rowVisible === false`` is only stamped on new posts. Older posts
  // omit it and keep showing "Posted by Newton".
  if (ledgerPost && ledgerPost.status === 'posted') {
    const recon = (reconciliationStatus || '').toLowerCase();
    const waitingOnRow = ledgerPost.rowVisible === false
      && recon !== 'matched'
      && recon !== 'manual_match';
    if (!waitingOnRow) {
      return { bg: '#e7f4ec', color: '#1d6b40', label: 'Posted by Newton', icon: '↩' };
    }
    return { bg: '#fdf3e3', color: '#a8520c', label: 'Checking PMS…', icon: '⏳' };
  }

  // A payment Newton held back or couldn't post (an insurance amount that
  // differs from the claim's estimate, a write the PMS refused, a payin that
  // didn't cover the invoice): staff post it in the PMS. Clears once the
  // reconciler matches the office's posting. A patient payment only counts
  // once it's paid: while it's processing, the settled webhook still retries
  // the post, and a hand entry now would book it twice.
  if (ledgerPost && (ledgerPost.status === 'skipped' || ledgerPost.status === 'failed')
      && (ledgerPost.kind === 'insurance' || invoiceStatus?.toLowerCase() === 'paid')) {
    const recon = (reconciliationStatus || '').toLowerCase();
    if (recon !== 'matched' && recon !== 'manual_match') {
      return { bg: '#fdecea', color: '#912018', label: 'Post in PMS', icon: '✗' };
    }
  }

  const status = invoiceStatus?.toLowerCase();

  // Only surface reconciliation for money that's settled or in flight.
  if (status !== 'paid' && status !== 'processing') {
    return null;
  }

  const normalizedStatus = (reconciliationStatus || 'pending').toLowerCase();

  const reconciliationStatusStyles = {
    pending: { bg: '#fdf3e3', color: '#a8520c', label: 'Checking PMS…', icon: '⏳' },
    matched: { bg: '#e7f4ec', color: '#1d6b40', label: 'Matched in PMS', icon: '✓' },
    amount_mismatch: { bg: '#fdeee4', color: '#9a3412', label: 'Amount mismatch', icon: '⚠' },
    not_found: { bg: '#fdecea', color: '#912018', label: 'Not in PMS', icon: '✗' },
    manual_match: { bg: '#e8f0f7', color: '#1f4e79', label: 'Matched manually', icon: '✓' }
  };

  // In flight (``processing``) and NOT posted by Newton: the reconciler now
  // also matches processing invoices (offices post immediately, without
  // waiting for settlement), so show the real match state. But a not-yet-found
  // processing payment is NOT a problem — the money is still in flight and the
  // ledger row may simply not exist yet — so keep it in the soft "Checking
  // PMS…" state rather than the red "Not in PMS". Red is reserved for PAID
  // invoices the reconciler has confirmed are genuinely absent.
  if (status === 'processing' && normalizedStatus === 'not_found') {
    return reconciliationStatusStyles.pending;
  }

  return reconciliationStatusStyles[normalizedStatus] || reconciliationStatusStyles.pending;
};

const INVOICE_PAGE_SIZE = 25;
// Matches the backend's RESOLVE_ATTENTION_BULK_MAX.
const RESOLVE_BULK_MAX = 200;

// Statuses that mean a payment went wrong and a human needs to follow up.
const ATTENTION_STATUSES = new Set([
  'failed',
  'returned',
  'chargeback_inquiry_action_required',
  'chargeback_dispute_action_required',
]);

// "Today, 9:32 AM" / "Yesterday, 4:10 PM" for recent timestamps, otherwise
// the plain date. Bare YYYY-MM-DD values are calendar dates with no time,
// so they are built as local dates (never shifted a day by UTC parsing).
const formatWhen = (value) => {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  if (m) return date;
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const now = new Date();
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay(d, now)) return `Today, ${time}`;
  if (sameDay(d, yesterday)) return `Yesterday, ${time}`;
  return date;
};

// A still-unpaid invoice whose card charge was declined: a terminal tap, a
// keyed or saved card, or a plan installment's auto-charge. The invoice
// stays ``pending`` so it can be charged again, which on its own reads as
// "not attempted yet". Returns the latest ``payment_failed`` activity entry
// (stamped by the ``payin.failed`` webhook), or null.
export const chargeFailure = (invoice) => {
  if (!invoice) return null;
  if ((invoice.status || '').toLowerCase() !== 'pending') return null;
  const failures = (invoice.activityLog || []).filter((a) => a.action === 'payment_failed');
  return failures.length ? failures[failures.length - 1] : null;
};

// Staff marked this invoice resolved ("Mark as resolved") and nothing has
// gone wrong since. A status change after the resolve (a return or
// chargeback on a paid invoice) or a charge declined after it brings
// it back. Mirrors the backend's _attention_resolution_current.
const isAttentionResolved = (invoice) => {
  const resolvedAt = invoice.attentionResolved?.at;
  if (!resolvedAt) return false;
  const resolvedStatus = invoice.attentionResolved.status;
  if (resolvedStatus && resolvedStatus !== invoice.status) return false;
  const failure = chargeFailure(invoice);
  return !(failure && new Date(failure.timestamp) > new Date(resolvedAt));
};

// Why an invoice counts toward "Needs attention": 'failed' (payment went
// wrong), 'declined' (a card charge was declined), 'pms' (paid but not matched in
// the PMS), or null. Shared by the KPI tile and its list filter so the count
// and the filtered rows can't disagree.
const attentionReason = (invoice) => {
  if (isAttentionResolved(invoice)) return null;
  // Whatever the invoice status became (cancelled, returned, refunded…).
  if (invoice.ledgerPost?.reversal?.status === 'needed') return 'pms';
  const s = String(invoice.status || '').toLowerCase();
  if (ATTENTION_STATUSES.has(s)) return 'failed';
  if (chargeFailure(invoice)) return 'declined';
  if (s === 'paid' || s === 'processing') {
    const recon = getReconciliationStatusStyle(invoice.reconciliationStatus, invoice.status, invoice.ledgerPost);
    if (recon && recon.label === 'Post in PMS') return 'pms';
    if (s === 'paid' && recon && (recon.label === 'Not in PMS' || recon.label === 'Amount mismatch')) return 'pms';
  }
  return null;
};

// Grey "Resolved" chip in place of a red "Not in PMS" / "Amount mismatch"
// once staff have marked the invoice resolved; null otherwise.
const resolvedReconStyle = (invoice, reconStyle) => {
  if (!reconStyle || !isAttentionResolved(invoice)) return null;
  if (!['Not in PMS', 'Amount mismatch', 'Post in PMS', 'Reverse in PMS'].includes(reconStyle.label)) return null;
  const r = invoice.attentionResolved;
  return {
    bg: '#eef0ee',
    color: '#3c4540',
    label: 'Resolved',
    icon: '✓',
    title: `${reconStyle.label} — marked resolved${r.byEmail ? ` by ${r.byEmail}` : ''} ${formatWhen(r.at)}`,
  };
};

const chargeFailureTitle = (invoice, entry) => {
  const d = entry.details || {};
  const card = d.cardBrand && d.cardLast4 ? `${d.cardBrand} •• ${d.cardLast4}` : 'Card';
  const why = d.refusalDescription || d.refusalCode || 'declined';
  const what = invoice.paymentPlanId ? 'Plan charge' : 'Charge';
  return `${what} declined ${formatWhen(entry.timestamp)} — ${card}: ${why}. Not paid; collect again.`;
};

// ``modalOnly``: render just the payment windows, without the invoice page, so
// another tab can take a payment in place (e.g. the Schedule tab). Either
//   ``collectRequest`` { method: 'virtual' | 'physical', patient, description }
//     — the "Collect payment" wizard, starting on that patient, or
//   ``chargeRequest`` (an existing invoice) — Send to Payment Terminal.
// Both hand off to the same waiting-for-payment window as the Invoices page.
// ``onClose`` fires once the windows have all closed and the result toast has
// shown.
const InvoiceTab = ({
  practiceId, chargeRequest, onChargeHandled, modalOnly = false, collectRequest = null, onClose,
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  // "Collect payment" dropdown (Dental-Intelligence-style). ``collectMenuOpen``
  // toggles the dropdown; ``collectMethod`` (null | 'physical' | 'virtual' |
  // 'request') drives the CollectPaymentModal wizard once a method is picked.
  const [collectMenuOpen, setCollectMenuOpen] = useState(false);
  // "Create invoice" dropdown — patient (copay) vs insurance invoice.
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [collectMethod, setCollectMethod] = useState(null);
  // True when the open create form is an "insurance" invoice (vs a copay
  // invoice). Insurance invoices are tagged on the doc, never trigger the
  // post-create "send link?" prompt, and have their SMS/email send buttons
  // disabled in the list — they're an internal A/R record, not something we
  // chase the patient to pay.
  const [isInsuranceInvoice, setIsInsuranceInvoice] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  // Insurance invoices: one row per patient the carrier's payment covers —
  // { key, patientId, patientName, patientPhone, patientEmail, claim, amount }.
  // A linked claim lets that row post to the PMS as an insurance payment on
  // it. One row saves as a single-patient insurance invoice; two or more as
  // a multi-patient one (``allocations``).
  const [insuranceRows, setInsuranceRows] = useState([]);
  // Optional carrier name shown on a multi-patient invoice.
  const [payerName, setPayerName] = useState('');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // Default line item seeded into the create-invoice form. Most invoices
  // staff cut here are a single "Copay" charge, so we pre-populate one
  // row with that description (and quantity locked to 1 — the qty input
  // is hidden in the UI) instead of forcing them to click "+ Add Item"
  // and type "Copay" every time.
  const makeDefaultLineItem = (description = 'Copay') => ({
    id: Date.now(),
    description,
    quantity: 1,
    price: ''
  });
  const [lineItems, setLineItems] = useState([makeDefaultLineItem()]);
  const [invoiceData, setInvoiceData] = useState({
    patientId: '',
    description: 'Estimated Copay',
    dueDate: ''
  });
  // Due date is optional and hidden behind a checkbox. Unchecked by default so
  // staff only set a due date when they explicitly want one; when unchecked the
  // dueDate stays '' and no due date is sent to the backend.
  const [showDueDate, setShowDueDate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Set when the backend rejects the create-invoice POST with
  // {conflict: true} because the same patient already has a pending
  // (non-plan) invoice. Drives the supersede confirmation dialog,
  // mirroring LedgerTab's UX so staff have a single mental model
  // for "duplicate invoice" prompts no matter which tab they
  // initiated the create from. Cleared on cancel or successful
  // supersede; null = no conflict pending.
  const [pendingInvoiceConflict, setPendingInvoiceConflict] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  // Whether this practice auto-posts captured payments to its PMS ledger
  // (Settings → autoPostLedgerEnabled). Drives the subtle info banner that
  // reminds staff copay payments post automatically while insurance
  // invoices do not.
  const [autoPostLedgerEnabled, setAutoPostLedgerEnabled] = useState(false);
  // Settings → autoPostInsuranceEnabled: insurance invoices with a linked
  // claim post too.
  const [autoPostInsuranceEnabled, setAutoPostInsuranceEnabled] = useState(false);
  // Clicking a patient name opens their PMS statement (the same read-only
  // ledger modal as the Ledger tab). Holds { contact_id, name, phone, email }
  // while open; null = closed.
  const [statementPatient, setStatementPatient] = useState(null);
  // Latest patient-ledger payload for the open statement, pushed by the
  // contained watch below so the modal updates without a page reload.
  const [statementLiveLedger, setStatementLiveLedger] = useState(null);
  const statementPatientRef = useRef(null);
  statementPatientRef.current = statementPatient;
  // 'pending' | 'paid' | 'superseded'. `superseded` rows are voided
  // invoices kept for audit; they're hidden from the Pending tab.
  const [invoiceViewMode, setInvoiceViewMode] = useState('pending');
  // List filters beyond the patient search: invoice type (all | patient |
  // insurance), and — on the Open tab only — status (all | pending |
  // processing). Plus the current page of the visible list.
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState('all');
  const [openStatusFilter, setOpenStatusFilter] = useState('all');
  const [invoicePage, setInvoicePage] = useState(1);
  // Sticky cross-tab filter that narrows the invoice list by patient
  // name / email / phone / id. Lives outside `patientSearchTerm`
  // (which drives the *create-invoice* patient picker) so the two
  // searches don't trample each other. Reset on full page refresh by
  // virtue of being component state.
  const [invoiceFilter, setInvoiceFilter] = useState('');
  // Set by clicking the "Needs attention" tile: narrows every tab to the
  // invoices that tile counts (see attentionReason).
  const [attentionOnly, setAttentionOnly] = useState(false);
  // Needs attention rows ticked for "Resolve selected" (invoice ids).
  const [resolveSelected, setResolveSelected] = useState(() => new Set());
  const [isBulkResolving, setIsBulkResolving] = useState(false);
  const invoiceListRef = useRef(null);
  // The All tab only exists under the Needs attention filter; dropping the
  // filter goes back to Open.
  useEffect(() => {
    if (!attentionOnly && invoiceViewMode === 'all') setInvoiceViewMode('pending');
  }, [attentionOnly, invoiceViewMode]);
  // Any change to what the list shows sends it back to page 1.
  useEffect(() => {
    setInvoicePage(1);
  }, [invoiceViewMode, invoiceFilter, invoiceTypeFilter, openStatusFilter, attentionOnly]);
  // Selection for "Resolve selected" never outlives the list it was made in.
  useEffect(() => {
    setResolveSelected(new Set());
  }, [invoiceViewMode, invoiceFilter, invoiceTypeFilter, attentionOnly]);
  
  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 2000);
  };
  
  // Plan detail drawer for plan installment invoices — the same drawer the
  // Payment Plans tab opens. Holds the getPaymentPlan payload.
  const [planDetail, setPlanDetail] = useState(null); // { plan, invoices }

  const openPlanDetail = async (planId) => {
    try {
      const resp = await getPaymentPlan(practiceId, planId);
      if (resp?.success) {
        setPlanDetail({ plan: resp.data, invoices: resp.invoices || [] });
      } else {
        showToast(resp?.error || 'Failed to load plan', 'error');
      }
    } catch (e) {
      showToast(String(e?.message || e), 'error');
    }
  };

  // SMS confirmation dialog state
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [pendingSmsInvoice, setPendingSmsInvoice] = useState(null);
  const [isSendingSms, setIsSendingSms] = useState(false);

  // Email confirmation dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [pendingEmailInvoice, setPendingEmailInvoice] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  // "Text & email" — one confirmation that sends the link on both channels.
  const [showBothDialog, setShowBothDialog] = useState(false);
  const [pendingBothInvoice, setPendingBothInvoice] = useState(null);
  const [isSendingBoth, setIsSendingBoth] = useState(false);

  // Receipt-email confirmation dialog state. Kept separate from the
  // invoice-email dialog above because the two send opposite things — a
  // request for money vs. confirmation of money received — and sharing
  // state would let one dialog's in-flight spinner disable the other.
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [pendingReceiptInvoice, setPendingReceiptInvoice] = useState(null);
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  // Editable in the dialog, prefilled from the invoice. Settled invoices
  // frequently have no patientEmail stored, and a receipt is worth sending
  // to an address staff can read off the chart.
  const [receiptEmail, setReceiptEmail] = useState('');

  // Send to device modal state
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [deviceModalInvoice, setDeviceModalInvoice] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  // When true, the device modal swaps its terminal-selection UI for the
  // manual-entry Virtual Terminal (one-time charge, card not saved).
  const [showVirtualTerminal, setShowVirtualTerminal] = useState(false);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isSendingToDevice, setIsSendingToDevice] = useState(false);
  // Staff's pre-tap card-type declaration for the "Send to device" flow.
  // Only used when the practice has surcharging on; the server uses this
  // to mint a replacement payin_config carrying the surcharge or debit
  // billing override before pushing to the terminal. ``null`` means "not
  // yet picked" (we disable Send Payment in that case).
  const [declaredCardType, setDeclaredCardType] = useState(null);
  // Staff-driven analogue of the public invoice page's "Save card for
  // next time" checkbox. Default-on matches the pre-checked default on
  // the public page; staff is expected to ask the patient verbally and
  // toggle off if they decline.
  const [saveCardOnPay, setSaveCardOnPay] = useState(true);

  // Practice-level payments toggles. Loaded once per mount; we only
  // really need ``surchargeEnabled`` + ``surchargePctCap`` for the
  // device-modal credit/debit picker, but other toggles can hang off
  // this state without another fetch later.
  const [practicePaymentsSettings, setPracticePaymentsSettings] = useState(null);
  
  // Active device presentation state (blocking modal)
  const [showPresentingModal, setShowPresentingModal] = useState(false);
  const [isSimulatingPresentation, setIsSimulatingPresentation] = useState(false);
  // Distinguishes the two device-flow modes that share the waiting modal:
  // 'payin'    — patient is charging an invoice (default)
  // 'savecard' — patient is tapping to vault their card (no charge)
  const [presentingMode, setPresentingMode] = useState('payin');
  const [isSavingCardOnDevice, setIsSavingCardOnDevice] = useState(false);
  const [presentingInvoice, setPresentingInvoice] = useState(null);
  const [presentingDeviceId, setPresentingDeviceId] = useState(null);
  const [isCancellingPresentation, setIsCancellingPresentation] = useState(false);
  // Billing preview returned by the send-to-device call (declared
  // card type + surcharge/debit override that was applied pre-tap).
  // Used purely to render the breakdown in the waiting modal so staff
  // can confirm what the terminal is presenting matches what they told
  // the patient. ``null`` means "no swap was made" (base config sent).
  const [presentingBillingPreview, setPresentingBillingPreview] = useState(null);
  const [presentingDeclaredCardType, setPresentingDeclaredCardType] = useState(null);
  // The ``payment_failed`` activity entry when the charge being presented
  // was declined; the waiting modal then says so in red instead of waiting.
  const [presentingDecline, setPresentingDecline] = useState(null);

  // Charge-card-on-file modal state. Mirrors the device-modal pattern:
  // open the modal, lazy-load the patient's saved cards, let the user
  // pick one (or default-pick the default card), confirm the charge.
  // After Rainforest accepts the payin we reuse `showPresentingModal`'s
  // 3s status poll to flip the row to "Processing" / "Paid" without
  // requiring a hard refresh.
  const [showChargeCardModal, setShowChargeCardModal] = useState(false);
  const [chargeCardInvoice, setChargeCardInvoice] = useState(null);
  const [chargeCardMethods, setChargeCardMethods] = useState([]);
  const [isLoadingChargeCards, setIsLoadingChargeCards] = useState(false);
  const [selectedVaultId, setSelectedVaultId] = useState('');
  const [isChargingCard, setIsChargingCard] = useState(false);
  const [chargeCardError, setChargeCardError] = useState(null);
  // When true, the Charge-Card modal swaps its body for the inline
  // StaffAddCardForm so staff can manually key a card on the patient's
  // behalf. Closing the form (success or cancel) sends us back to the
  // saved-card list, which is refetched on success so the new card
  // shows up immediately.
  const [showAddCardForm, setShowAddCardForm] = useState(false);

  // Timeline modal state
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineInvoice, setTimelineInvoice] = useState(null);
  // Set while the Cancel Invoice button in the Timeline modal is in
  // flight — disables the button and swaps the label so staff don't
  // double-click and fire two cancels.
  const [isCancellingInvoice, setIsCancellingInvoice] = useState(false);
  const [practiceTimezone, setPracticeTimezone] = useState('America/New_York');

  // Close the Timeline modal on Escape. Held off while a cancel is in
  // flight so a stray keypress can't dismiss the modal mid-request.
  useEffect(() => {
    if (!showTimelineModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isCancellingInvoice) {
        setShowTimelineModal(false);
        setTimelineInvoice(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showTimelineModal, isCancellingInvoice]);
  // Practice display name — used as the header on the printable invoice.
  const [practiceName, setPracticeName] = useState('');
  // Raw practice.address (string or object), formatted at print time and
  // shown under the name on the printable invoice.
  const [practiceAddress, setPracticeAddress] = useState(null);

  // Helper function to get date 2 weeks from today
  const getTwoWeeksFromToday = () => {
    const today = new Date();
    const twoWeeksLater = new Date(today.getTime() + (14 * 24 * 60 * 60 * 1000));
    return twoWeeksLater.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };


  // Search patients using API
  const searchPatients = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const data = await fetchFromPaymentsAPI(`/api/payments/patients/search?practiceId=${practiceId}&q=${encodeURIComponent(searchTerm)}`);
      
      if (data && data.success) {
        setSearchResults(data.patients || []);
      } else {
        console.error('Patient search failed:', data?.error || 'Unknown error');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching patients:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [practiceId]);

  const loadInvoices = useCallback(async () => {
    if (!practiceId) return;

    setIsLoadingInvoices(true);
    try {
      const data = await fetchFromPaymentsAPI(`/api/payments/invoices?practiceId=${practiceId}`);

      if (data && data.success) {
        setInvoices(data.invoices || []);
      } else {
        console.error('Failed to load invoices:', data?.error || 'Unknown error');
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
    } finally {
      setIsLoadingInvoices(false);
    }
  }, [practiceId]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchPatients(patientSearchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [patientSearchTerm, searchPatients]);

  // Load invoices when component mounts or practiceId changes. Not needed
  // for a modal-only charge: the payment poller fetches its own status.
  useEffect(() => {
    if (modalOnly) return;
    loadInvoices();
  }, [loadInvoices, modalOnly]);

  // Fetch practice info (timezone + payments toggles). Re-uses one
  // round-trip to populate everything the tab needs about the practice
  // — timezone for date formatting and ``payments`` for the device
  // modal's credit/debit picker.
  useEffect(() => {
    const fetchPracticeInfo = async () => {
      if (!practiceId) return;
      try {
        const data = await fetchFromPaymentsAPI(`/api/payments/practices/${practiceId}/info`);
        if (data?.success && data?.practice) {
          if (data.practice.timezone) setPracticeTimezone(data.practice.timezone);
          if (data.practice.payments) {
            setPracticePaymentsSettings(data.practice.payments);
            setAutoPostLedgerEnabled(!!data.practice.payments.autoPostLedgerEnabled);
            setAutoPostInsuranceEnabled(
              !!data.practice.payments.autoPostInsuranceEnabled
              && data.practice.payments.autoPostInsuranceSupported !== false,
            );
          }
          const pName = data.practice.name || data.practice.practiceName || data.practice.businessName;
          if (pName) setPracticeName(pName);
          if (data.practice.address) setPracticeAddress(data.practice.address);
        }
      } catch (error) {
        console.error('Error fetching practice info:', error);
      }
    };
    fetchPracticeInfo();
  }, [practiceId]);

  // Lookup maps for the "Posted by Newton" detail: resolve the bare Dentrix
  // codes stored on ``ledgerPost`` (providerId, paymentTypeId) to the human
  // names the practice synced from its PMS, so staff see "Tebay, Tyler
  // (XX04)" instead of "XX04". Empty maps when config isn't loaded — the UI
  // falls back to showing the raw code.
  const ledgerProviderNameById = useMemo(() => {
    const map = {};
    for (const p of (practicePaymentsSettings?.ledgerProviders || [])) {
      if (p && p.id != null) map[String(p.id)] = p.name || String(p.id);
    }
    return map;
  }, [practicePaymentsSettings]);
  const ledgerPaymentTypeNameById = useMemo(() => {
    const map = {};
    for (const t of (practicePaymentsSettings?.ledgerPaymentTypes || [])) {
      if (t && t.id != null) {
        map[String(t.id)] = t.description || t.name || String(t.id);
      }
    }
    return map;
  }, [practicePaymentsSettings]);

  // The set of PMS-ledger check_numbers (references) Newton has posted, so the
  // patient statement modal can tint those exact rows light green.
  const newtonReferences = useMemo(() => {
    const s = new Set();
    for (const inv of invoices) {
      const lp = inv?.ledgerPost;
      if (!lp || lp.status !== 'posted') continue;
      if (lp.reference) s.add(lp.reference);
      // Broken-down payments map to several slice references.
      for (const sp of lp.splits || []) {
        if (sp?.reference) s.add(sp.reference);
      }
    }
    return s;
  }, [invoices]);

  // Poll invoice status while a device PAYIN is being presented. Save-
  // card flows don't change invoice status (they vault a payment method
  // without charging), so they're handled by the separate vault poller
  // below.
  useEffect(() => {
    if (!showPresentingModal || !presentingInvoice) return;
    if (presentingMode !== 'payin') return;

    const pollInterval = setInterval(async () => {
      try {
        const data = await fetchFromPaymentsAPI(`/api/payments/invoices?practiceId=${practiceId}`);
        if (data?.success && data.invoices) {
          const updatedInvoice = data.invoices.find(inv => inv.id === presentingInvoice.id);
          const status = updatedInvoice?.status?.toLowerCase();
          // Declined: the invoice goes back to ``pending`` with a new
          // ``failedAt``. Keep polling, since a retry on the terminal can
          // still go through.
          if (updatedInvoice && status === 'pending' && updatedInvoice.failedAt
            && updatedInvoice.failedAt !== presentingInvoice.failedAt) {
            setInvoices(data.invoices);
            setPresentingDecline(chargeFailure(updatedInvoice) || { details: {} });
          }
          // Close once the charge is in flight. When this practice posts to
          // the PMS, wait until ``ledgerPost`` is on the doc too — that stamp
          // is written after the status flip, and the "Checking PMS…" watch
          // below only starts once ``rowVisible === false`` is in state.
          if (updatedInvoice && (status === 'processing' || status === 'paid')) {
            const ledgerStatus = updatedInvoice.ledgerPost?.status;
            const stillPosting = autoPostLedgerEnabled && !ledgerStatus;
            setInvoices(data.invoices);
            if (stillPosting) return;
            setShowPresentingModal(false);
            setPresentingInvoice(null);
            setPresentingDeviceId(null);
            setPresentingMode('payin');
            showToast(status === 'paid' ? 'Payment completed!' : 'Payment processing!');
          }
        }
      } catch (error) {
        console.error('Error polling invoice status:', error);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [showPresentingModal, presentingInvoice, presentingMode, practiceId, autoPostLedgerEnabled]);

  // A decline belongs to the presentation it happened on.
  useEffect(() => {
    setPresentingDecline(null);
  }, [presentingInvoice]);

  // Posted payments whose ledger row is not in our cache yet. While any are
  // on screen, refetch only those patients' ledgers (not the invoice list)
  // until the Newton reference shows up. Keeps going for as long as the
  // invoice stays in "Checking PMS…".
  const pendingLedgerWatchKey = useMemo(() => {
    const parts = [];
    for (const inv of invoices) {
      const lp = inv?.ledgerPost;
      if (!lp || lp.status !== 'posted' || lp.rowVisible !== false || !lp.reference || !inv.patientId) continue;
      const recon = (inv.reconciliationStatus || '').toLowerCase();
      if (recon === 'matched' || recon === 'manual_match') continue;
      parts.push(`${inv.patientId}:${lp.reference}`);
    }
    parts.sort();
    return parts.join('|');
  }, [invoices]);

  useEffect(() => {
    if (!practiceId || !pendingLedgerWatchKey) return undefined;
    const watches = new Map();
    for (const part of pendingLedgerWatchKey.split('|')) {
      const splitAt = part.indexOf(':');
      if (splitAt < 0) continue;
      const patientId = part.slice(0, splitAt);
      const reference = part.slice(splitAt + 1);
      if (!watches.has(patientId)) watches.set(patientId, []);
      watches.get(patientId).push(reference);
    }
    let cancelled = false;
    const tick = async () => {
      for (const [patientId, refs] of watches) {
        if (cancelled) return;
        try {
          const data = await getPatientLedger(practiceId, patientId);
          if (cancelled || !data?.success) continue;
          const seen = new Set(
            (data.events || []).map((e) => e.check_number).filter(Boolean)
          );
          const landed = refs.filter((r) => seen.has(r));
          if (landed.length) {
            const landedSet = new Set(landed);
            setInvoices((prev) => prev.map((inv) => {
              const ref = inv.ledgerPost?.reference;
              if (!ref || !landedSet.has(ref) || inv.ledgerPost?.rowVisible) return inv;
              return { ...inv, ledgerPost: { ...inv.ledgerPost, rowVisible: true } };
            }));
          }
          const open = statementPatientRef.current;
          if (open && String(open.contact_id) === String(patientId)) {
            setStatementLiveLedger({ contactId: patientId, data, at: Date.now() });
          }
        } catch (err) {
          console.error('[ledger-watch] patient ledger read failed', err);
        }
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [practiceId, pendingLedgerWatchKey]);

  // Poll the vault while a save-card-on-device flow is active. The
  // ``payment_method.activated`` webhook upserts the new card on the
  // backend, but since no invoice/payin status changes there's nothing
  // for the payin poller above to react to. We snapshot the patient's
  // existing vault IDs at modal-open time and auto-close as soon as a
  // new id appears.
  const [savecardBaselineIds, setSavecardBaselineIds] = useState(null);

  useEffect(() => {
    if (!showPresentingModal || !presentingInvoice) return;
    if (presentingMode !== 'savecard') return;
    if (!presentingInvoice.patientId) return;

    let cancelled = false;

    // Establish the baseline once per modal open.
    const seedBaseline = async () => {
      try {
        const result = await listPatientPaymentMethods(
          practiceId,
          presentingInvoice.patientId,
        );
        if (cancelled) return;
        const ids = (result?.paymentMethods || []).map((c) => c.id);
        setSavecardBaselineIds(new Set(ids));
      } catch (err) {
        console.error('Error seeding savecard baseline:', err);
        if (!cancelled) setSavecardBaselineIds(new Set());
      }
    };

    if (savecardBaselineIds === null) {
      seedBaseline();
    }

    const pollInterval = setInterval(async () => {
      if (savecardBaselineIds === null) return; // wait for baseline
      try {
        const result = await listPatientPaymentMethods(
          practiceId,
          presentingInvoice.patientId,
        );
        if (cancelled) return;
        const cards = result?.paymentMethods || [];
        const newCard = cards.find((c) => !savecardBaselineIds.has(c.id) && c.status === 'ACTIVE');
        if (newCard) {
          setShowPresentingModal(false);
          setPresentingInvoice(null);
          setPresentingDeviceId(null);
          setPresentingMode('payin');
          setSavecardBaselineIds(null);
          showToast(`Card saved: ${newCard.brand || 'Card'} •• ${newCard.last4 || ''}`);
        }
      } catch (err) {
        console.error('Error polling vault for new card:', err);
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [showPresentingModal, presentingInvoice, presentingMode, practiceId, savecardBaselineIds]);

  // Reset the baseline whenever the modal closes (any path) so the next
  // open starts with a fresh snapshot.
  useEffect(() => {
    if (!showPresentingModal) setSavecardBaselineIds(null);
  }, [showPresentingModal]);

  const addLineItem = () => {
    const newLineItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      price: ''
    };
    setLineItems([...lineItems, newLineItem]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const calculateTotal = () => {
    if (isInsuranceInvoice) return rowsTotal(insuranceRows).toFixed(2);
    return lineItems.reduce((total, item) => {
      return total + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0);
    }, 0).toFixed(2);
  };

  // Helper to add activity to local invoice state
  const addActivityToLocalInvoice = (invoiceId, action, details = {}) => {
    const newActivity = {
      action,
      timestamp: new Date().toISOString(),
      details
    };
    setInvoices(prev => prev.map(inv => 
      inv.id === invoiceId 
        ? { ...inv, activityLog: [...(inv.activityLog || []), newActivity] }
        : inv
    ));
  };

  // "Mark as resolved" on a Needs attention row: staff confirm they've
  // handled it (e.g. found the payment on the PMS ledger). Merges the
  // server's copy so the row leaves the list and the count drops at once.
  const handleResolveAttention = async (invoice) => {
    const confirmed = window.confirm(
      'Mark this payment as resolved?\n\n'
      + 'It will be removed from Needs attention. Only do this once you have handled it, '
      + 'for example confirmed the payment is posted in your PMS ledger.'
    );
    if (!confirmed) return;
    try {
      const result = await resolveInvoiceAttention(practiceId, invoice.id);
      if (result && result.success) {
        showToast('Marked as resolved');
        if (result.invoice) {
          setInvoices(prev => prev.map(inv =>
            inv.id === invoice.id ? { ...inv, ...result.invoice } : inv
          ));
        }
      } else {
        showToast(result?.message || result?.error || 'Failed to mark as resolved', 'error');
      }
    } catch (e) {
      console.error('Error resolving invoice attention', e);
      showToast('Error marking as resolved', 'error');
    }
  };

  // "Resolve selected": one bulk request; every outcome is reported, and the
  // saved invoices are merged so resolved rows leave the list at once.
  const handleBulkResolve = async (selected) => {
    if (!selected.length || isBulkResolving) return;
    const total = selected.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);
    const n = selected.length;
    const confirmed = window.confirm(
      `Mark ${n} payment${n === 1 ? '' : 's'} (${formatCurrency(total)}) as resolved?\n\n`
      + 'They will be removed from Needs attention. Only do this once you have handled them, '
      + 'for example confirmed the payments are posted in your PMS ledger.'
    );
    if (!confirmed) return;
    setIsBulkResolving(true);
    try {
      const result = await resolveInvoiceAttentionBulk(practiceId, selected.map((inv) => inv.id));
      if (result && result.success) {
        const byId = new Map((result.invoices || []).map((inv) => [inv.id, inv]));
        setInvoices((prev) => prev.map((inv) => (byId.has(inv.id) ? { ...inv, ...byId.get(inv.id) } : inv)));
        const c = result.counts || {};
        const done = (c.resolved || 0) + (c.already || 0);
        const failed = (c.not_found || 0) + (c.conflict || 0) + (c.error || 0);
        showToast(
          `${done} marked as resolved${failed ? ` · ${failed} couldn't be updated, try again` : ''}`,
          failed ? 'error' : 'success',
        );
        // Keep only the ones that failed ticked, so a retry is one click.
        const failedIds = new Set((result.results || [])
          .filter((r) => r.outcome !== 'resolved' && r.outcome !== 'already')
          .map((r) => r.id));
        setResolveSelected(failedIds);
      } else {
        showToast(result?.message || result?.error || 'Failed to mark as resolved', 'error');
      }
    } catch (e) {
      console.error('Error bulk resolving invoice attention', e);
      showToast('Error marking as resolved', 'error');
    } finally {
      setIsBulkResolving(false);
    }
  };

  // Channel sends shared by the single-channel dialogs and "Text & email".
  // Each records its activity on success and returns { ok, error }; they
  // throw on network errors so callers keep their own error copy.
  const deliverInvoiceSms = async (invoice, phoneNumber) => {
    const data = await fetchFromPaymentsAPI(`/api/payments/invoices/${invoice.id}/send-sms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        practiceId,
        phoneNumber
      })
    });
    if (data && data.success) {
      logInvoiceActivity(practiceId, invoice.id, 'sms_sent', { phoneNumber });
      addActivityToLocalInvoice(invoice.id, 'sms_sent', { phoneNumber });
      return { ok: true };
    }
    return { ok: false, error: data?.error || 'Unknown error' };
  };

  const deliverInvoiceEmail = async (invoice, email) => {
    const result = await sendInvoiceEmail(practiceId, invoice.id, email);
    if (result && result.success) {
      logInvoiceActivity(practiceId, invoice.id, 'email_sent', { email });
      addActivityToLocalInvoice(invoice.id, 'email_sent', { email });
      return { ok: true };
    }
    return { ok: false, error: result?.error || 'Unknown error' };
  };

  // Send SMS with invoice link
  const sendInvoiceSms = async (invoice, phoneNumber) => {
    if (!invoice || !phoneNumber) return false;
    
    setIsSendingSms(true);
    try {
      const sent = await deliverInvoiceSms(invoice, phoneNumber);
      if (sent.ok) {
        showToast('SMS sent successfully!');
        return true;
      } else {
        showToast(`Failed to send SMS: ${sent.error}`, 'error');
        return false;
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      showToast('Error sending SMS', 'error');
      return false;
    } finally {
      setIsSendingSms(false);
      setShowSmsDialog(false);
      setPendingSmsInvoice(null);
    }
  };

  // Send email with invoice link
  const sendInvoiceEmailHandler = async (invoice, email) => {
    if (!invoice || !email) return false;
    
    setIsSendingEmail(true);
    try {
      const sent = await deliverInvoiceEmail(invoice, email);
      if (sent.ok) {
        showToast('Email sent successfully!');
        return true;
      } else {
        showToast(`Failed to send email: ${sent.error}`, 'error');
        return false;
      }
    } catch (error) {
      console.error('Error sending email:', error);
      showToast('Error sending email', 'error');
      return false;
    } finally {
      setIsSendingEmail(false);
      setShowEmailDialog(false);
      setPendingEmailInvoice(null);
    }
  };

  // Send the payment link by text AND email. Sequential on purpose: both
  // endpoints read-modify-write the same invoice doc (no etag), so running
  // them concurrently could drop one channel's notification record. One
  // toast at the end says exactly what went out.
  const sendInvoiceBoth = async (invoice) => {
    if (!invoice || !invoice.patientPhone || !invoice.patientEmail) return false;
    setIsSendingBoth(true);
    const attempt = async (fn, fallback) => {
      try {
        return await fn();
      } catch (error) {
        console.error(fallback, error);
        return { ok: false, error: fallback };
      }
    };
    try {
      const sms = await attempt(() => deliverInvoiceSms(invoice, invoice.patientPhone), 'Error sending SMS');
      const email = await attempt(() => deliverInvoiceEmail(invoice, invoice.patientEmail), 'Error sending email');
      if (sms.ok && email.ok) {
        showToast('Payment link sent by text and email!');
      } else if (sms.ok) {
        showToast(`Text sent, but email failed: ${email.error}`, 'error');
      } else if (email.ok) {
        showToast(`Email sent, but text failed: ${sms.error}`, 'error');
      } else {
        showToast(`Failed to send text and email: ${sms.error}`, 'error');
      }
      return sms.ok && email.ok;
    } finally {
      setIsSendingBoth(false);
      setShowBothDialog(false);
      setPendingBothInvoice(null);
    }
  };

  // Email the patient a PDF receipt for a settled invoice. The PDF is
  // rendered server-side (backend/receipt_pdf.py) rather than from the
  // print view here, so the attachment is real selectable text.
  const sendInvoiceReceiptHandler = async (invoice, email) => {
    if (!invoice || !email) return false;

    setIsSendingReceipt(true);
    try {
      const result = await sendInvoiceReceipt(practiceId, invoice.id, email);

      if (result && result.success) {
        showToast('Receipt sent successfully!');
        // The backend already appends this to the invoice's activityLog;
        // mirror it locally so the Timeline modal reflects the send without
        // a refetch.
        addActivityToLocalInvoice(invoice.id, 'receipt_emailed', { email });
        return true;
      }
      showToast(
        `Failed to send receipt: ${result?.message || result?.error || 'Unknown error'}`,
        'error'
      );
      return false;
    } catch (error) {
      console.error('Error sending receipt:', error);
      showToast('Error sending receipt', 'error');
      return false;
    } finally {
      setIsSendingReceipt(false);
      setShowReceiptDialog(false);
      setPendingReceiptInvoice(null);
    }
  };

  // Why this invoice can't be sent a receipt, or null if it can: a payment
  // must have settled (there's nothing to receipt before that), and insurance
  // invoices are claims against the carrier rather than the patient.
  //
  // Mirrors receipt_block_reason() in backend/receipt_pdf.py, which the route
  // enforces — keep the two in step or the button will offer a send that the
  // API then refuses. Deliberately NOT gated on a patient email being on
  // file: the dialog lets staff type one, which matters because plenty of
  // settled invoices have no email stored.
  const receiptBlockReason = (invoice) => {
    if (invoice.invoiceType === 'insurance') {
      return 'Insurance invoices are claims against the carrier — receipts are not sent to patients';
    }
    const status = (invoice.status || '').toLowerCase();
    if (status !== 'paid' && status !== 'processing') {
      return `No payment to receipt yet — invoice is "${invoice.status}"`;
    }
    return null;
  };

  // Open the "email receipt" dialog. Offered from the Open tab's ⋯ menu
  // and the Paid tab's Receipt button; both respect receiptBlockReason.
  const startReceipt = (invoice) => {
    if (receiptBlockReason(invoice)) return;
    setPendingReceiptInvoice(invoice);
    setReceiptEmail(invoice.patientEmail || '');
    setShowReceiptDialog(true);
  };

  // An insurance invoice is paid with the carrier's card, which is never
  // saved to the patient (the server refuses it too), so its "Save card"
  // box is shown locked off.
  const deviceModalIsInsurance = deviceModalInvoice?.invoiceType === 'insurance';

  // Open device modal and load devices from DB cache
  const openDeviceModal = async (invoice) => {
    setDeviceModalInvoice(invoice);
    setShowDeviceModal(true);
    setShowVirtualTerminal(false);
    setSelectedDeviceId('');
    // Force staff to re-pick credit vs debit each time the modal
    // opens — defaulting to a sticky value across invoices is the
    // worst possible UX here (a stale CREDIT default would silently
    // surcharge the next debit-tap patient).
    setDeclaredCardType(null);
    // Default-on for save-card matches the public page's pre-checked
    // checkbox. Staff toggles off if the patient explicitly declines.
    setSaveCardOnPay(true);

    // Load devices from DB cache (refresh=false)
    setIsLoadingDevices(true);
    try {
      const result = await listDevices(practiceId, false);
      if (result.success) {
        const deviceList = Array.isArray(result.devices) ? result.devices : [];
        setDevices(deviceList);
        // Pre-select the default device, or first device if no default
        if (result.default_device_id) {
          setSelectedDeviceId(result.default_device_id);
        } else if (deviceList.length > 0) {
          setSelectedDeviceId(deviceList[0].device_registration_id);
        }
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
      setDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  // Cross-tab "Create & Charge" handoff: when the Ledger tab creates an
  // invoice and asks to charge it immediately, the Dashboard parks the new
  // invoice in ``chargeRequest`` and switches here. Open the terminal/device
  // charge modal for it, then clear the request so it fires only once.
  useEffect(() => {
    if (!chargeRequest) return;
    openDeviceModal(chargeRequest);
    onChargeHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeRequest]);

  // Modal-only "Collect payment": open the wizard for the requested method.
  useEffect(() => {
    if (!modalOnly || !collectRequest?.method) return;
    setCollectMethod(collectRequest.method);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOnly, collectRequest]);

  // Modal-only lifecycle: once a payment window has opened, tell the caller
  // to unmount us when they've all closed (cancelled, or paid) — after the
  // result toast has had its 2s, so "Payment completed!" is still seen.
  const chargeFlowOpenedRef = useRef(false);
  useEffect(() => {
    if (!modalOnly) return;
    if (collectMethod || showDeviceModal || showPresentingModal || showChargeCardModal) {
      chargeFlowOpenedRef.current = true;
      return;
    }
    if (chargeFlowOpenedRef.current && !toast.show) onClose?.();
  }, [modalOnly, collectMethod, showDeviceModal, showPresentingModal, showChargeCardModal, toast.show, onClose]);

  // CollectPaymentModal owns the full front half of the flow (pick a patient,
  // enter an amount, and — only on submit — create the invoice and send it to
  // the terminal / charge the keyed card). Once it's created + charged/queued
  // the invoice, it hands off here so we can show the existing "waiting for
  // payment" modal (polling, cancel-presentation, sandbox Simulate-Tap) rather
  // than duplicating that surface. No orphan invoice is left if staff abandon
  // the wizard, because nothing is created until they submit.
  const handleCollectPresent = ({
    invoice,
    deviceId = null,
    mode = 'payin',
    billingPreview = null,
    declaredCardType: declared = null,
  }) => {
    setCollectMethod(null);
    setCollectMenuOpen(false);
    if (!invoice) return;
    setPresentingInvoice(invoice);
    setPresentingDeviceId(deviceId);
    setPresentingMode(mode);
    setPresentingBillingPreview(billingPreview);
    setPresentingDeclaredCardType(declared);
    setShowPresentingModal(true);
    loadInvoices();
  };

  // Send invoice to device. When the practice has surcharging on, the
  // staff *must* declare credit/debit before this fires (the picker is
  // a hard gate in the modal); otherwise we send with no declared type
  // and the server uses the original payin_config unchanged.
  const handleSendToDevice = async () => {
    if (!selectedDeviceId || !deviceModalInvoice) return;

    const surchargeOn = !!practicePaymentsSettings?.surchargeEnabled;
    if (surchargeOn && !declaredCardType) {
      showToast('Please select Credit or Debit before sending to terminal', 'error');
      return;
    }

    setIsSendingToDevice(true);
    try {
      const result = await sendInvoiceToDevice(
        practiceId,
        deviceModalInvoice.id,
        selectedDeviceId,
        declaredCardType,
        saveCardOnPay && !deviceModalIsInsurance,
      );

      if (result.success) {
        const auditDetails = {
          deviceId: selectedDeviceId,
          declaredCardType: result.declaredCardType || declaredCardType || null,
          billingPreview: result.billingPreview || null,
          saveCardOnPay: !!saveCardOnPay && !deviceModalIsInsurance,
        };
        logInvoiceActivity(practiceId, deviceModalInvoice.id, 'sent_to_device', auditDetails);
        addActivityToLocalInvoice(deviceModalInvoice.id, 'sent_to_device', auditDetails);
        // Close the selection modal and show the presenting modal
        setPresentingInvoice(deviceModalInvoice);
        setPresentingDeviceId(selectedDeviceId);
        setPresentingMode('payin');
        setPresentingBillingPreview(result.billingPreview || null);
        setPresentingDeclaredCardType(result.declaredCardType || declaredCardType || null);
        setShowDeviceModal(false);
        setShowPresentingModal(true);
        setDeviceModalInvoice(null);
        setSelectedDeviceId('');
        setDeclaredCardType(null);
      } else {
        showToast(`Failed to send to device: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error sending to device:', error);
      showToast('Error sending to device', 'error');
    } finally {
      setIsSendingToDevice(false);
    }
  };

  // Tokenize-only on a terminal — patient taps to save the card without
  // being charged. We reuse the same waiting modal as a device payin
  // (cancel-presentation / sandbox simulate both still apply); the
  // ``presentingMode='savecard'`` flag swaps the copy and the post-success
  // routing. The card lands in the vault via the payment_method.activated
  // webhook, so there's no further client-side write to make.
  //
  // NOTE: The "+ Add Card" button in the Send-to-Device modal is
  // currently hidden (see the action-row JSX), so this handler has no
  // call site right now. We intentionally keep it (and the
  // 'savecard' branch in the waiting modal) so the button can be
  // un-hidden by restoring just the JSX without re-plumbing the
  // backend call. Hence the eslint suppression.
  // eslint-disable-next-line no-unused-vars
  const handleAddCardOnDevice = async () => {
    if (!selectedDeviceId || !deviceModalInvoice) return;

    setIsSavingCardOnDevice(true);
    try {
      const result = await saveCardOnDevice(practiceId, deviceModalInvoice.id, selectedDeviceId);

      if (result.success) {
        logInvoiceActivity(practiceId, deviceModalInvoice.id, 'save_card_on_device', { deviceId: selectedDeviceId });
        addActivityToLocalInvoice(deviceModalInvoice.id, 'save_card_on_device', { deviceId: selectedDeviceId });
        setPresentingInvoice(deviceModalInvoice);
        setPresentingDeviceId(selectedDeviceId);
        setPresentingMode('savecard');
        // Save-card flow doesn't carry a billing preview — clear any
        // stale preview from a prior payin send so the breakdown
        // section doesn't render the wrong context.
        setPresentingBillingPreview(null);
        setPresentingDeclaredCardType(null);
        setShowDeviceModal(false);
        setShowPresentingModal(true);
        setDeviceModalInvoice(null);
        setSelectedDeviceId('');
      } else {
        showToast(`Failed to send save-card to device: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error saving card on device:', error);
      showToast('Error sending save-card to device', 'error');
    } finally {
      setIsSavingCardOnDevice(false);
    }
  };

  // Charge-card-on-file: open the modal and lazy-load the patient's
  // saved cards. Mirrors `openDeviceModal` but reads from the vault
  // instead of the devices cache.
  // Reload the patient's saved cards into the charge-card modal. Used
  // both on initial open and after the inline "Add card" form lands a
  // new card in the vault so the radio list refreshes with the new row
  // (and the new row is auto-selected). Returns the loaded card array.
  const reloadChargeCardMethods = async (invoice, { selectVaultId } = {}) => {
    if (!invoice?.patientId) return [];
    setIsLoadingChargeCards(true);
    try {
      const result = await listPatientPaymentMethods(
        practiceId,
        invoice.patientId,
        { invoiceId: invoice.id },
      );
      if (result?.success) {
        const cards = (result.paymentMethods || []).filter(c => c.status === 'ACTIVE');
        setChargeCardMethods(cards);
        if (selectVaultId && cards.some(c => c.id === selectVaultId)) {
          setSelectedVaultId(selectVaultId);
        } else {
          // Selection priority for the radio default:
          //   1. The card the invoice itself was bound to. Plan
          //      installments stamp ``paymentMethodVaultId`` at mint
          //      time and we re-stamp it whenever the plan's saved
          //      card changes — so manual collection from this modal
          //      uses the *plan's* current card, not the patient's
          //      generic default (which can drift away from the plan
          //      card, e.g. patient added a personal card after
          //      enrolling on a parent's card).
          //   2. The patient's default card (legacy behavior, used
          //      for one-off invoices that aren't part of a plan).
          //   3. The first active card on file (fallback).
          const invVaultId = invoice?.paymentMethodVaultId;
          const invoicePinned = invVaultId
            ? cards.find(c => c.id === invVaultId)
            : null;
          const defaultCard = invoicePinned
            || cards.find(c => c.isDefault)
            || cards[0];
          if (defaultCard) setSelectedVaultId(defaultCard.id);
        }
        return cards;
      }
      setChargeCardMethods([]);
      return [];
    } catch (err) {
      console.error('Error reloading saved cards:', err);
      setChargeCardMethods([]);
      return [];
    } finally {
      setIsLoadingChargeCards(false);
    }
  };

  const openChargeCardModal = async (invoice) => {
    setChargeCardInvoice(invoice);
    setShowChargeCardModal(true);
    setSelectedVaultId('');
    setChargeCardError(null);
    setChargeCardMethods([]);
    setShowAddCardForm(false);
    if (!invoice?.patientId) return;

    setIsLoadingChargeCards(true);
    try {
      // Pass invoiceId so the server attaches a per-card surcharge
      // preview — that's what powers the "+ 3% surcharge applies"
      // hint and the dynamic "Pay $103.00" radio-row affordance below.
      const result = await listPatientPaymentMethods(
        practiceId,
        invoice.patientId,
        { invoiceId: invoice.id },
      );
      if (result?.success) {
        const cards = (result.paymentMethods || []).filter(c => c.status === 'ACTIVE');
        setChargeCardMethods(cards);
        // Pre-select the invoice's pinned card when present (plan
        // installments stamp ``paymentMethodVaultId`` so manual
        // charges hit the plan's current card, not the patient's
        // generic default). See ``reloadChargeCardMethods`` for the
        // same rule + rationale.
        const invVaultId = invoice?.paymentMethodVaultId;
        const invoicePinned = invVaultId
          ? cards.find(c => c.id === invVaultId)
          : null;
        const defaultCard = invoicePinned
          || cards.find(c => c.isDefault)
          || cards[0];
        if (defaultCard) setSelectedVaultId(defaultCard.id);
      } else {
        setChargeCardMethods([]);
      }
    } catch (err) {
      console.error('Error loading saved cards:', err);
      setChargeCardMethods([]);
    } finally {
      setIsLoadingChargeCards(false);
    }
  };

  // Trigger the actual charge against the selected saved card.
  // Returns immediately on Rainforest's 202 — the polling loop set up
  // by `showPresentingModal` will flip the row to "Processing" / "Paid"
  // when the payin webhook arrives.
  const handleChargeCardOnFile = async () => {
    if (!selectedVaultId || !chargeCardInvoice) return;
    setIsChargingCard(true);
    setChargeCardError(null);
    try {
      const { ok, status, data } = await chargeInvoiceWithSavedCard(
        practiceId,
        chargeCardInvoice.id,
        selectedVaultId,
      );
      if (!ok || !data?.success) {
        const msg = data?.details?.error
          || data?.error
          || `Charge failed (HTTP ${status})`;
        setChargeCardError(msg);
        return;
      }
      addActivityToLocalInvoice(chargeCardInvoice.id, 'card_on_file_charged', {
        vaultId: selectedVaultId,
        source: 'staff_dashboard',
      });
      // Re-use the device-modal's polling pattern for the
      // "Processing → Paid" flip without a hard refresh.
      setPresentingInvoice(chargeCardInvoice);
      setPresentingDeviceId(null);
      // Card-on-file flow has its own surcharge UX in the charge modal
      // itself — clear any stale preview from a prior device send so
      // the waiting modal doesn't render terminal-flow context here.
      setPresentingBillingPreview(null);
      setPresentingDeclaredCardType(null);
      setShowChargeCardModal(false);
      setShowPresentingModal(true);
      setChargeCardInvoice(null);
      setSelectedVaultId('');
    } catch (err) {
      console.error('Error charging card on file:', err);
      setChargeCardError('Network error — please try again.');
    } finally {
      setIsChargingCard(false);
    }
  };

  // Cancel device presentation
  const handleCancelPresentation = async () => {
    if (!presentingDeviceId || !presentingInvoice) return;
    
    setIsCancellingPresentation(true);
    try {
      const result = await cancelDevicePresentation(practiceId, presentingDeviceId);
      
      if (result.success) {
        setShowPresentingModal(false);
        setPresentingInvoice(null);
        setPresentingDeviceId(null);
        setPresentingMode('payin');
      } else {
        showToast(`Failed to cancel: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error cancelling presentation:', error);
      showToast('Error cancelling', 'error');
    } finally {
      setIsCancellingPresentation(false);
    }
  };

  // Sandbox-only convenience: trigger Rainforest's simulate-device-interaction
  // endpoint from inside the "Waiting for Payment" modal so QA doesn't have
  // to bounce to the Device Management tab to flip a virtual terminal out
  // of PRESENTING. The webhook still drives invoice reconciliation; this
  // just supplies the tap.
  const handleSimulatePresentation = async (presetId) => {
    if (!presentingDeviceId || !presetId) return;
    const preset = SIMULATE_DEVICE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;

    setIsSimulatingPresentation(true);
    try {
      const result = await simulateDeviceInteraction(
        practiceId,
        presentingDeviceId,
        preset.event,
        preset.card,
      );
      if (result.success) {
        showToast(`Simulated: ${preset.label}`);
        // Don't auto-close — the existing payin-status poll will flip the
        // modal to its terminal state once Rainforest fires the webhook.
      } else {
        showToast(`Simulate failed: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error simulating presentation:', error);
      showToast('Error simulating', 'error');
    } finally {
      setIsSimulatingPresentation(false);
    }
  };

  // ``options.supersede`` opts in to replacing any existing pending
  // invoice for the same (practice, patient). The first attempt
  // always submits with supersede=false; on a duplicate-pending
  // 200-conflict the backend returns the existing invoice and we
  // park it in ``pendingInvoiceConflict`` so the dialog can drive
  // the next step. A "Yes, replace" click re-invokes this handler
  // with supersede=true. Mirrors LedgerTab.handleCreateInvoice so
  // staff get the same UX no matter where they initiated the create
  // from. Backend filters out plan installment invoices from the
  // dedup query, so this only fires for genuine one-off conflicts.
  // The insurance form's rows as an invoice payload: one row is a regular
  // single-patient insurance invoice (optionally with ``claim``); several
  // become ``allocations`` on one invoice, charged once for the total.
  const buildInsurancePayload = () => {
    const rows = insuranceRows;
    const amountOf = (r) => Math.round((parseFloat(r.amount) || 0) * 100) / 100;
    const description = invoiceData.description || 'Insurance Payment';
    if (rows.length === 1) {
      const [r] = rows;
      return {
        patientId: r.patientId,
        patientName: r.patientName,
        patientPhone: r.patientPhone || '',
        patientEmail: r.patientEmail || '',
        description,
        dueDate: invoiceData.dueDate,
        lineItems: [{ description, quantity: 1, price: amountOf(r) }],
        total: calculateTotal(),
        invoiceType: 'insurance',
        ...(r.claim ? { claim: r.claim } : {}),
      };
    }
    const payer = payerName.trim();
    return {
      // The invoice sits on the first patient; ``allocations`` holds them all.
      patientId: rows[0].patientId,
      patientName: `${payer || 'Insurance payment'} · ${rows.length} patients`,
      patientPhone: '',
      patientEmail: '',
      description,
      dueDate: invoiceData.dueDate,
      lineItems: rows.map((r) => ({ description: `Insurance payment — ${r.patientName}`, quantity: 1, price: amountOf(r) })),
      total: calculateTotal(),
      invoiceType: 'insurance',
      ...(payer ? { payerName: payer } : {}),
      allocations: rows.map((r) => ({
        patientId: r.patientId,
        patientName: r.patientName,
        amount: amountOf(r),
        ...(r.claim ? { claim: r.claim } : {}),
      })),
    };
  };

  const resetInsuranceForm = () => {
    setInsuranceRows([]);
    setPayerName('');
  };

  const handleSaveInvoice = async ({ supersede = false, chargeNow = false } = {}) => {
    if (isInsuranceInvoice) {
      if (insuranceRows.length === 0) {
        showToast('Add at least one patient', 'error');
        return;
      }
      const missing = insuranceRows.find((r) => !(parseFloat(r.amount) > 0));
      if (missing) {
        showToast(`Enter an amount for ${missing.patientName}`, 'error');
        return;
      }
    } else if (!selectedPatient) {
      showToast('Please select a patient', 'error');
      return;
    }
    if (!isInsuranceInvoice && lineItems.length === 0) {
      showToast('Please add at least one line item', 'error');
      return;
    }
    if (calculateTotal() <= 0) {
      showToast('Invoice total must be greater than $0', 'error');
      return;
    }

    const invoiceData_payload = isInsuranceInvoice ? buildInsurancePayload() : {
      patientId: selectedPatient.patientId || selectedPatient.id,
      patientName: selectedPatient.name,
      patientPhone: selectedPatient.phoneNumber || '',
      patientEmail: selectedPatient.email || '',
      description: invoiceData.description,
      dueDate: invoiceData.dueDate,
      lineItems: lineItems.map(item => ({
        description: item.description,
        quantity: parseFloat(item.quantity) || 0,
        price: parseFloat(item.price) || 0
      })),
      total: calculateTotal(),
      invoiceType: 'copay',
    };

    setIsSaving(true);
    if (!supersede) {
      // Clear any stale conflict on a fresh attempt; the backend
      // will re-set it below if a conflict still exists. The
      // supersede=true retry path keeps the dialog open until the
      // request resolves so the user has consistent feedback.
      setPendingInvoiceConflict(null);
    }
    try {
      const data = await createInvoice(practiceId, invoiceData_payload, { supersede });

      if (data && data.success) {
        const createdInvoice = data.invoice;
        const patientForSms = selectedPatient;
        const wasInsurance = isInsuranceInvoice;
        resetInsuranceForm();

        setShowCreateForm(false);
        setIsInsuranceInvoice(false);
        setSelectedPatient(null);
        setPatientSearchTerm('');
        setSearchResults([]);
        setLineItems([makeDefaultLineItem()]);
        setInvoiceData({ patientId: '', description: 'Estimated Copay', dueDate: '' });
        setShowDueDate(false);
        setPendingInvoiceConflict(null);

        loadInvoices();

        // "Create & Charge": skip the send-link prompt entirely and jump
        // straight into the Send-to-Terminal flow for the new invoice so
        // the patient can tap their card at the desk.
        if (chargeNow && createdInvoice) {
          showToast(supersede ? 'Invoice replaced.' : 'Invoice created.');
          openDeviceModal(createdInvoice);
        } else if (wasInsurance) {
          // Insurance invoices are an internal A/R record — never prompt to
          // send (or auto-send) a payment link to the patient.
          showToast(supersede ? 'Insurance invoice replaced.' : 'Insurance invoice saved!');
        } else if (patientForSms?.phoneNumber && createdInvoice?.publicHash) {
          setPendingSmsInvoice({
            invoice: createdInvoice,
            patient: patientForSms
          });
          setShowSmsDialog(true);
        } else {
          showToast(supersede ? 'Invoice replaced.' : 'Invoice saved!');
        }
      } else if (data && data.conflict) {
        // Duplicate-pending. Park the existing invoice metadata so
        // the dialog can render details (id, total, age, public
        // link) and offer a one-click "replace" that re-runs this
        // handler with supersede=true. Preserve the chargeNow intent so
        // a replace from a "Create & Charge" click still charges.
        setPendingInvoiceConflict({
          existing: data.existing_invoice || {},
          existingCount: data.existing_count || 1,
          patient: isInsuranceInvoice && insuranceRows.length === 1
            ? { name: insuranceRows[0].patientName, patientId: insuranceRows[0].patientId }
            : selectedPatient,
          chargeNow,
        });
      } else {
        showToast(`Failed to save invoice: ${data?.error || 'Unknown error'}`, 'error');
        console.error('Invoice save failed:', data);
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      showToast('Error saving invoice', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // A bare "YYYY-MM-DD" (e.g. the ledger ``paymentDate``) is a calendar
      // date with no time/zone. `new Date('YYYY-MM-DD')` parses it as UTC
      // midnight, so `.toLocaleDateString()` shifts it back a day in any
      // negative-offset browser timezone (Pacific staff saw Sep 28 as
      // "Sep 27"). Build a LOCAL date from the parts so it renders verbatim.
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateString).trim());
      const d = m
        ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        : new Date(dateString);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Render a stored phone number in standard US format for the printed
  // document. Cosmos holds phones in E.164 (+14155550148), which is right
  // for dialling and wrong on a document a patient reads; the country code
  // is dropped since the practice is US. Anything unrecognisable (an
  // international number, an extension) passes through untouched rather
  // than being mangled.
  //
  // Mirrors format_phone() in backend/receipt_pdf.py — the emailed PDF must
  // show the same string as the printout.
  const formatPhoneForPrint = (raw) => {
    if (!raw) return '';
    const text = String(raw).trim();
    let digits = text.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 7) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return text;
  };

  // Render the practice address as one comma-separated line. `practice.address`
  // is polymorphic across the fleet: a legacy hand-entered string, or the
  // {streetAddress, city, state, zip} object the Practices tab writes (some
  // docs carry Zuub-style address1/address2 keys). State and ZIP are joined
  // with a space, not a comma, per US convention.
  //
  // Mirrors format_address() in backend/receipt_pdf.py.
  const formatAddressForPrint = (raw) => {
    if (!raw) return '';
    if (typeof raw === 'string') return raw.replace(/\s+/g, ' ').trim();
    if (typeof raw !== 'object') return '';
    const street = raw.streetAddress || raw.address1 || raw.street || '';
    const suite = raw.address2 || raw.suite || '';
    const city = raw.city || '';
    const region = [raw.state, raw.zip || raw.zipCode || raw.postalCode]
      .map((p) => String(p || '').trim()).filter(Boolean).join(' ');
    return [street, suite, city, region]
      .map((p) => String(p || '').trim()).filter(Boolean)
      .join(', ').replace(/\s+/g, ' ').trim();
  };

  // Open a print-friendly rendering of the invoice in a new window and
  // trigger the browser print dialog. Self-contained HTML (built from the
  // invoice doc) so it doesn't depend on loading the public page.
  const printInvoice = (invoice) => {
    if (!invoice) return;
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    const items = Array.isArray(invoice.lineItems) ? invoice.lineItems : [];
    const rows = items.map((it) => {
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.price) || 0;
      return `<tr>
        <td class="desc">${esc(it.description || '')}</td>
        <td class="num qty">${qty}</td>
        <td class="num">${formatCurrency(price)}</td>
        <td class="num amt">${formatCurrency(qty * price)}</td>
      </tr>`;
    }).join('');

    // --- money model -----------------------------------------------------
    // A printed copy of a settled invoice doubles as the patient's receipt,
    // so it carries the card that was charged. When a surcharge applied we
    // also have to itemise it — otherwise the printed total understates what
    // actually hit the card.
    const card = invoice.paymentCard || {};
    const cardBrand = card.brand_desc || card.brand || '';
    const cardLabel = [cardBrand, card.last_4 ? `•••• ${card.last_4}` : '']
      .filter(Boolean).join(' ') || invoice.paymentMethodType || '';
    const paidAtLabel = invoice.paidAt ? formatTimestampInTimezone(invoice.paidAt) : '';

    const surcharge = invoice.surchargeApplied || {};
    const surchargeCents = Math.round(Number(surcharge.surchargeCents) || 0);
    const baseCents = Math.round((Number(invoice.total) || 0) * 100);
    let chargedCents;
    if (typeof invoice.actualPayinAmountCents === 'number') {
      chargedCents = Math.round(invoice.actualPayinAmountCents);
    } else if (typeof surcharge.totalCents === 'number') {
      chargedCents = Math.round(surcharge.totalCents);
    } else {
      chargedCents = baseCents + surchargeCents;
    }

    // Settled invoices read as a receipt ("here's what you paid"); open ones
    // as a bill ("here's what you owe"). Drives the masthead wording.
    const status = (invoice.status || 'pending').toLowerCase();
    const isSettled = status === 'paid' || status === 'processing';
    const heroCents = isSettled ? chargedCents : baseCents + surchargeCents;
    const statusTone = {
      paid: 'ok',
      processing: 'warn',
      pending: 'warn',
      cancelled: 'bad',
      canceled: 'bad',
      failed: 'bad',
    }[status] || 'quiet';

    // The browser's own print header (which carried the date) is suppressed
    // by the @page rule below, so stamp the generation time ourselves.
    const generatedAtLabel = formatTimestampInTimezone(new Date().toISOString());

    const practiceAddressLine = formatAddressForPrint(practiceAddress);

    const trow = (label, value, cls = '') =>
      `<div class="trow${cls ? ` ${cls}` : ''}"><span>${label}</span><span class="num">${value}</span></div>`;
    const pctLabel = surcharge.surchargePct != null ? ` (${surcharge.surchargePct}%)` : '';
    const totalsBlock = [
      surchargeCents > 0 ? trow('Subtotal', formatCurrency(baseCents / 100)) : '',
      surchargeCents > 0
        ? trow(`Card surcharge${esc(pctLabel)}`, formatCurrency(surchargeCents / 100))
        : '',
      trow(isSettled ? 'Total charged' : 'Total due', formatCurrency(heroCents / 100), 'grand'),
    ].filter(Boolean).join('');

    // Three-column ledger strip under the masthead, hairline-divided.
    const cell = (label, body) =>
      `<div class="cell"><div class="eyebrow">${label}</div>${body}</div>`;
    const metaBlock = [
      cell('Billed to', [
        `<span class="val">${esc(invoice.patientName || '—')}</span>`,
        // Deliberately no patient ID — an internal PMS identifier means
        // nothing to the person reading the receipt, and the Reference cell
        // already identifies the document for support purposes.
        invoice.patientEmail ? `<span class="sub">${esc(invoice.patientEmail)}</span>` : '',
        invoice.patientPhone ? `<span class="sub">${esc(formatPhoneForPrint(invoice.patientPhone))}</span>` : '',
      ].filter(Boolean).join('')),
      cell('Reference', [
        `<span class="val mono">${esc(invoice.id || '—')}</span>`,
        invoice.invoiceType === 'insurance' ? '<span class="sub">Insurance claim</span>' : '',
      ].filter(Boolean).join('')),
      cell('Dates', [
        invoice.createdAt
          ? `<span class="val">Issued ${esc(formatDate(invoice.createdAt))}</span>` : '',
        invoice.dueDate ? `<span class="sub">Due ${esc(formatDate(invoice.dueDate))}</span>` : '',
      ].filter(Boolean).join('') || '<span class="val">—</span>'),
    ].join('');

    // Tear-off stub: the payment facts, set off by a perforation-style rule.
    const facts = [
      // Brand + last four only. The CREDIT/DEBIT classification is an
      // internal billing detail (it drives surcharging) and reads as noise
      // on the patient's copy.
      cardLabel ? ['Method', esc(cardLabel)] : null,
      paidAtLabel ? ['Paid', esc(paidAtLabel)] : null,
    ].filter(Boolean);
    const paymentBlock = facts.length
      ? `<section class="stub">
    <div class="eyebrow">Payment</div>
    <div class="facts">${facts.map(([k, v]) => `<div class="fact"><span class="k">${k}</span><span class="v">${v}</span></div>`).join('')}</div>
  </section>`
      : '';

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${isSettled ? 'Receipt' : 'Invoice'} ${esc(invoice.id || '')}</title>
  <style>
    /* Print-first "editorial ledger": ink on paper, a serif display voice
       against tabular sans data, hairline rules instead of boxes. Fonts are
       drawn from the system stack on purpose — a webfont would race the
       print() call and risk printing in a fallback face. */
    :root {
      --ink: #14181d;
      --ink-2: #4d5661;
      --ink-3: #98a1ad;
      --rule: #e3e7eb;
      --rule-2: #c6ccd3;
      --serif: "Iowan Old Style", Charter, "Palatino Linotype", Palatino, Georgia, serif;
      --sans: "Avenir Next", "Segoe UI", "Helvetica Neue", Helvetica, sans-serif;
      --mono: "SF Mono", ui-monospace, "Cascadia Mono", Consolas, monospace;
    }
    * { box-sizing: border-box; }
    html, body { margin: 0; }
    body {
      font-family: var(--sans);
      font-size: 13px;
      line-height: 1.5;
      color: var(--ink);
      padding: 40px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .eyebrow { font-size: 9px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-3); }
    .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .mono { font-family: var(--mono); font-size: 12px; }

    /* Masthead: practice name + address left, status + hero amount right. */
    .masthead { display: flex; align-items: flex-start; justify-content: space-between; gap: 32px; }
    .masthead h1 { font-family: var(--serif); font-size: 27px; font-weight: 600; letter-spacing: -.01em; margin: 5px 0 0; }
    .mast-addr { font-size: 11px; color: var(--ink-2); margin-top: 4px; max-width: 34em; }
    .hero { text-align: right; white-space: nowrap; }
    .hero .eyebrow { margin-top: 11px; }
    .hero .amount { font-family: var(--serif); font-size: 34px; font-weight: 600; letter-spacing: -.02em; font-variant-numeric: tabular-nums; margin-top: 1px; }
    .keyline { height: 2px; background: var(--ink); margin-top: 18px; }

    .chip { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 9px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
    .chip-ok { background: #e2efe9; color: #0d5c4d; }
    .chip-warn { background: #fbf0da; color: #855400; }
    .chip-bad { background: #fae5e3; color: #8f2219; }
    /* Ledger strip — three hairline-divided columns of document metadata. */
    .ledger { display: flex; border-bottom: 1px solid var(--rule); }
    .ledger .cell { flex: 1; padding: 16px 0 18px 20px; border-left: 1px solid var(--rule); }
    .ledger .cell:first-child { flex: 1.5; padding-left: 0; border-left: 0; }
    .cell .val { display: block; margin-top: 7px; font-weight: 600; }
    .cell .sub { display: block; color: var(--ink-2); font-size: 12px; }

    .note { font-family: var(--serif); font-size: 15px; margin-top: 24px; }

    table { width: 100%; border-collapse: collapse; margin-top: 26px; }
    thead th { padding: 0 0 8px; border-bottom: 1px solid var(--rule-2); font-size: 9px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--ink-3); text-align: left; }
    thead th.num { text-align: right; }
    tbody td { padding: 11px 0; border-bottom: 1px solid var(--rule); vertical-align: top; }
    td.desc { padding-right: 20px; }
    td.num { padding-left: 20px; }
    td.qty { color: var(--ink-2); }
    td.amt { font-weight: 600; }
    td.empty { color: var(--ink-3); font-style: italic; }

    .totals { width: 272px; margin: 15px 0 0 auto; }
    .trow { display: flex; justify-content: space-between; gap: 20px; padding: 5px 0; color: var(--ink-2); }
    .trow .num { color: var(--ink); }
    .trow.grand { margin-top: 6px; padding-top: 10px; border-top: 1px solid var(--rule-2); color: var(--ink); font-weight: 700; }
    .trow.grand .num { font-family: var(--serif); font-size: 19px; font-weight: 600; }

    /* Perforation-style rule — nods to a tear-off receipt stub. */
    .stub { margin-top: 34px; padding-top: 18px; border-top: 1px dashed var(--rule-2); }
    .facts { display: flex; flex-wrap: wrap; gap: 44px; margin-top: 11px; }
    .fact .k { display: block; margin-bottom: 2px; font-size: 9px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--ink-3); }
    .fact .v { font-weight: 600; }

    .colophon { display: flex; justify-content: space-between; gap: 20px; margin-top: 32px; padding-top: 12px; border-top: 1px solid var(--rule); font-size: 10px; color: var(--ink-3); }

    /* Zero the page margin so the browser has no room to stamp its own
       header/footer (the dashboard URL + "1/1" page count) onto what is
       effectively the patient's receipt. Whitespace comes from padding. */
    @page { size: auto; margin: 0; }
    @media print { body { padding: 14mm; } }
  </style>
</head>
<body>
  <header class="masthead">
    <div>
      <div class="eyebrow">${isSettled ? 'Receipt' : 'Invoice'}</div>
      <h1>${esc(practiceName || 'Invoice')}</h1>
      ${practiceAddressLine ? `<div class="mast-addr">${esc(practiceAddressLine)}</div>` : ''}
    </div>
    <div class="hero">
      <span class="chip chip-${statusTone}">${esc(status)}</span>
      <div class="eyebrow">${isSettled ? 'Total charged' : 'Amount due'}</div>
      <div class="amount">${formatCurrency(heroCents / 100)}</div>
    </div>
  </header>
  <div class="keyline"></div>
  <div class="ledger">${metaBlock}</div>
  ${invoice.description ? `<div class="note">${esc(invoice.description)}</div>` : ''}
  <table>
    <thead>
      <tr><th>Description</th><th class="num">Qty</th><th class="num">Price</th><th class="num">Amount</th></tr>
    </thead>
    <tbody>${rows || '<tr><td colspan="4" class="empty">No line items</td></tr>'}</tbody>
  </table>
  <div class="totals">${totalsBlock}</div>
  ${paymentBlock}
  <footer class="colophon">
    <span>${esc(practiceName || '')}</span>
    <span>Generated ${esc(generatedAtLabel)}</span>
  </footer>
</body>
</html>`;

    // Render into a hidden iframe and print from there — no new tab/window.
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      // Defer removal so the print dialog has fully detached first.
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1000);
    };

    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        win.focus();
        // Remove the iframe once printing finishes / is cancelled.
        win.onafterprint = cleanup;
        win.print();
      } catch (err) {
        console.error('Print failed:', err);
        showToast('Could not open print dialog', 'error');
        cleanup();
      }
    };

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
  };

  // Helper function to format timestamp in practice timezone
  const formatTimestampInTimezone = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        timeZone: practiceTimezone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return isoString;
    }
  };

  // Helper function to get action display label
  const getActionLabel = (action) => {
    const labels = {
      'invoice_created': 'Invoice Created',
      'sms_sent': 'SMS Sent',
      'email_sent': 'Email Sent',
      'receipt_emailed': 'Receipt Emailed',
      'link_copied': 'Link Copied',
      'sent_to_device': 'Sent to Device',
      'save_card_on_device': 'Save Card on Device',
      'card_on_file_charged': 'Card on File Charged',
      'card_charged': 'Card Charged',
      'payment_processing': 'Payment Processing',
      'payment_processing_sms_sent': 'Processing SMS Sent',
      'payment_completed': 'Payment Completed',
      'payment_completed_sms_sent': 'Payment SMS Sent',
      'payment_failed': 'Payment Failed',
      'invoice_superseded': 'Invoice Superseded',
      'invoice_cancelled': 'Invoice Cancelled',
      'replaced_pending_invoice': 'Replaced Pending Invoice',
      'plan_card_repointed': 'Plan Card Updated',
      'ledger_posted': 'Posted to PMS Ledger',
      'ledger_post_failed': 'PMS Ledger Post Failed',
      'pms_reconciled': 'PMS Reconciled',
      'attention_resolved': 'Marked Resolved',
    };
    if (labels[action]) return labels[action];
    return String(action || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Aggregate totals by status bucket for the summary row above the
  // invoice list, scoped to the current calendar month in the
  // practice's timezone (so an East-Coast practice's month rolls over
  // at their local midnight, not the viewer's). Each bucket is scoped
  // by the timestamp that actually put the invoice in that state:
  //   - Completed → paidAt (money collected this month)
  //   - Processing → when the payin started processing
  //   - Pending → createdAt
  // We exclude superseded/cancelled invoices since they are not
  // collectible and would inflate the "Pending" bucket.
  const statusSummary = useMemo(() => {
    // "YYYY-MM" for a timestamp as seen in the practice timezone.
    const monthKeyInTz = (value) => {
      if (!value) return null;
      const d = new Date(value);
      if (isNaN(d.getTime())) return null;
      try {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: practiceTimezone,
          year: 'numeric',
          month: '2-digit',
        }).formatToParts(d);
        const year = parts.find((p) => p.type === 'year')?.value;
        const month = parts.find((p) => p.type === 'month')?.value;
        return year && month ? `${year}-${month}` : null;
      } catch {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      }
    };
    const currentMonthKey = monthKeyInTz(new Date());
    const inThisMonth = (value) => currentMonthKey && monthKeyInTz(value) === currentMonthKey;

    // When the payin started processing. ``processingAt`` is never
    // actually stamped on the doc today, so we fall back to the
    // ``payin.processing`` webhook's own time (mirrors the timeline).
    const processingStartedAt = (inv) => {
      const hooks = Array.isArray(inv.payinWebhooks) ? inv.payinWebhooks : [];
      const hook = hooks.find((w) => w.event_type === 'payin.processing');
      return (
        inv.processingAt ||
        (hook && (hook?.data?.data?.updated_at || hook.received_at)) ||
        inv.updatedAt
      );
    };

    const sums = { pending: 0, processing: 0, completed: 0 };
    for (const inv of invoices) {
      const s = String(inv.status || '').toLowerCase();
      const amount = Number(inv.total) || 0;
      if (s === 'pending') {
        if (inThisMonth(inv.createdAt)) sums.pending += amount;
      } else if (s === 'processing' || s === 'authorized') {
        if (inThisMonth(processingStartedAt(inv))) sums.processing += amount;
      } else if (s === 'paid') {
        if (inThisMonth(inv.paidAt)) sums.completed += amount;
      }
    }
    return sums;
  }, [invoices, practiceTimezone]);

  // "Needs attention" KPI: payments that went wrong (any month), plus paid
  // invoices the reconciler couldn't match in the PMS. Uses the same
  // reconciliation style function as the table, so the count and the red
  // chips on the Paid tab can't disagree.
  const needsAttention = useMemo(() => {
    const counts = { failed: 0, pms: 0, declined: 0 };
    for (const inv of invoices) {
      const reason = attentionReason(inv);
      if (reason) counts[reason] += 1;
    }
    return { ...counts, total: counts.failed + counts.pms + counts.declined };
  }, [invoices]);

  // Human-readable current month label (e.g. "September 2026") for the
  // summary row heading.
  const currentMonthLabel = useMemo(() => {
    try {
      return new Date().toLocaleDateString(undefined, {
        timeZone: practiceTimezone,
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
  }, [practiceTimezone]);

  return (
    <div>
      {/* Modal-only "Collect payment" (the page renders its own below). */}
      {modalOnly && collectMethod && (
        <CollectPaymentModal
          practiceId={practiceId}
          method={collectMethod}
          settings={practicePaymentsSettings}
          initialPatient={collectRequest?.patient || null}
          initialDescription={collectRequest?.description || ''}
          onCancel={() => setCollectMethod(null)}
          onToast={showToast}
          onPresent={handleCollectPresent}
        />
      )}

      {!modalOnly && (
      <div className="pm-inv">
        {/* Page header: month context + auto-post status on the left, the
            two page actions on the right. Stays visible while the create
            form is open, like the old header row. */}
        <div className="pm-inv-header">
          <div className="pm-inv-header-meta">
            <span className="pm-inv-month" title={`Totals below are for ${currentMonthLabel}`}>
              {currentMonthLabel}
            </span>
            {/* PMS auto-post status, always shown so staff know whether to
                post by hand. One chip per setting: patient invoices
                (autoPostLedgerEnabled) and insurance invoices
                (autoPostInsuranceEnabled, which only posts while patient
                auto-posting is on too). */}
            <span
              className="pm-status-chip"
              title={autoPostLedgerEnabled
                ? 'Captured card payments on patient invoices post to the PMS ledger automatically.'
                : 'Auto-posting is off — post patient payments in the PMS yourself. Turn it on in Settings → Ledger posting.'}
            >
              <span className={`pm-status-dot${autoPostLedgerEnabled ? '' : ' pm-status-dot-warn'}`} aria-hidden="true" />
              Patient invoices auto-post to PMS · {autoPostLedgerEnabled ? 'On' : 'Off'}
              <Icon name="info" size={13} />
            </span>
            {(() => {
              const insuranceOn = autoPostLedgerEnabled && autoPostInsuranceEnabled;
              return (
                <span
                  className="pm-status-chip"
                  title={insuranceOn
                    ? 'Insurance invoices linked to a claim post to the PMS as an insurance payment on that claim. Ones without a claim still need posting by hand.'
                    : autoPostInsuranceEnabled
                      ? 'Insurance auto-posting is on, but it only runs while patient auto-posting is on too — post insurance payments in the PMS yourself until then.'
                      : 'Insurance invoices are not auto-posted — post those payments in the PMS yourself. Turn it on in Settings → Ledger posting.'}
                >
                  <span className={`pm-status-dot${insuranceOn ? '' : ' pm-status-dot-warn'}`} aria-hidden="true" />
                  Insurance invoices auto-post to the claim · {insuranceOn ? 'On' : 'Off'}
                  <Icon name="info" size={13} />
                </span>
              );
            })()}
          </div>
          <div className="pm-inv-header-actions">
            {/* Create invoice — patient (copay) vs insurance invoice. Each
                option opens the create form exactly as the two separate
                buttons used to. */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="pm-btn"
                onClick={() => {
                  setCollectMenuOpen(false);
                  setCreateMenuOpen((o) => !o);
                }}
                aria-haspopup="menu"
                aria-expanded={createMenuOpen}
              >
                <Icon name="plus" size={15} strokeWidth={2} />
                Create invoice
                <Icon name="chevron-down" size={14} />
              </button>
              {createMenuOpen && (
                <>
                  <div className="pm-menu-backdrop" onClick={() => setCreateMenuOpen(false)} />
                  <div role="menu" className="pm-menu" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0 }}>
                    {[
                      {
                        id: 'patient',
                        icon: 'file-text',
                        label: 'Create patient invoice',
                        open: () => {
                          setShowCreateForm(true);
                          setIsInsuranceInvoice(false);
                          setShowDueDate(false);
                          setInvoiceData({
                            patientId: '',
                            description: 'Estimated Copay',
                            dueDate: ''
                          });
                          setLineItems([makeDefaultLineItem()]);
                        },
                      },
                      {
                        id: 'insurance',
                        icon: 'clipboard-list',
                        label: 'Create insurance invoice',
                        open: () => {
                          setShowCreateForm(true);
                          setIsInsuranceInvoice(true);
                          setShowDueDate(false);
                          setInvoiceData({
                            patientId: '',
                            description: 'Insurance Payment',
                            dueDate: ''
                          });
                          setLineItems([makeDefaultLineItem('Insurance Payment')]);
                        },
                      },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        role="menuitem"
                        className="pm-menu-item"
                        onClick={() => {
                          setCreateMenuOpen(false);
                          opt.open();
                        }}
                      >
                        <Icon name={opt.icon} size={15} />
                        <span className="pm-menu-item-text">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Collect payment — starts from a patient (not a pre-existing
                invoice) and routes into the physical or virtual terminal. */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="pm-btn-primary"
                onClick={() => {
                  setCreateMenuOpen(false);
                  setCollectMenuOpen((o) => !o);
                }}
                aria-haspopup="menu"
                aria-expanded={collectMenuOpen}
              >
                Collect payment
                <Icon name="chevron-down" size={14} />
              </button>
              {collectMenuOpen && (
                <>
                  <div className="pm-menu-backdrop" onClick={() => setCollectMenuOpen(false)} />
                  <div role="menu" className="pm-menu" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0 }}>
                    {[
                      { id: 'virtual', icon: 'credit-card', label: 'Virtual terminal' },
                      { id: 'physical', icon: 'smartphone', label: 'Physical terminal' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        role="menuitem"
                        className="pm-menu-item"
                        onClick={() => {
                          setCollectMenuOpen(false);
                          setCollectMethod(opt.id);
                        }}
                      >
                        <Icon name={opt.icon} size={15} />
                        <span className="pm-menu-item-text">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Month summary. Pending / Processing / Collected are this
            month's totals (same buckets as before); Needs attention counts
            everything currently failing, regardless of month. */}
        <div className="pm-kpis">
          <KpiTile
            label="Pending"
            value={formatCurrency(statusSummary.pending)}
            valueColor={statusSummary.pending > 0 ? 'var(--pm-warn)' : undefined}
            sub={`Awaiting payment · created in ${currentMonthLabel}`}
          />
          <KpiTile
            label="Processing"
            value={formatCurrency(statusSummary.processing)}
            valueColor={statusSummary.processing > 0 ? 'var(--pm-info)' : undefined}
            sub="Paid by the patient, not yet settled"
          />
          <KpiTile
            label="Collected"
            value={formatCurrency(statusSummary.completed)}
            valueColor={statusSummary.completed > 0 ? 'var(--pm-ok)' : undefined}
            sub={`Paid in ${currentMonthLabel}`}
          />
          <KpiTile
            label="Needs attention"
            value={String(needsAttention.total)}
            valueColor={needsAttention.total > 0 ? 'var(--pm-danger)' : undefined}
            sub={
              needsAttention.total > 0
                ? [
                    needsAttention.failed > 0 && `${needsAttention.failed} failed or disputed`,
                    needsAttention.declined > 0 && `${needsAttention.declined} ${needsAttention.declined === 1 ? 'charge' : 'charges'} declined`,
                    needsAttention.pms > 0 && `${needsAttention.pms} not matched in PMS`,
                  ].filter(Boolean).join(' · ')
                : 'Nothing to follow up'
            }
            title="Failed, returned or disputed payments, payment-plan installments whose auto-charge was declined, plus paid invoices the reconciler could not find (or found with a different amount) in the PMS. Click to list them."
            onClick={needsAttention.total > 0 ? () => {
              // Show exactly what the tile counts: drop the other filters and
              // list every flagged invoice, whatever its status, on the All tab.
              setInvoiceFilter('');
              setInvoiceTypeFilter('all');
              setOpenStatusFilter('all');
              setAttentionOnly(true);
              setInvoiceViewMode('all');
              invoiceListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } : undefined}
          />
        </div>

        {collectMethod && (
          <CollectPaymentModal
            practiceId={practiceId}
            method={collectMethod}
            settings={practicePaymentsSettings}
            onCancel={() => setCollectMethod(null)}
            onToast={showToast}
            onPresent={handleCollectPresent}
          />
        )}

        {showCreateForm ? (
          <div style={{ border: '1px solid #e3e7e4', borderRadius: '12px', padding: '20px', backgroundColor: '#fff', textAlign: 'left' }}>
            <h4 style={{ margin: '0 0 20px 0', color: '#2b302d' }}>
              {isInsuranceInvoice ? 'Create Insurance Invoice' : 'Create New Invoice'}
            </h4>
            
            {/* Patient Search */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {isInsuranceInvoice ? 'Add patient:' : 'Search Patient:'}
              </label>
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={patientSearchTerm}
                onChange={(e) => setPatientSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e3e7e4',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              
              {patientSearchTerm && (
                <div style={{
                  border: '1px solid #e3e7e4',
                  borderTop: 'none',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: 'white'
                }}>
                  {isSearching ? (
                    <div style={{ padding: '10px', color: '#6b716d', textAlign: 'center' }}>
                      Searching patients...
                    </div>
                  ) : (
                    <>
                      {searchResults.map(patient => (
                        <div
                          key={patient.id}
                          onClick={() => {
                            if (isInsuranceInvoice) {
                              // Insurance: each pick adds a patient row (the
                              // same patient twice is fine — two claims).
                              setInsuranceRows((rows) => [...rows, {
                                key: `${patient.patientId || patient.id}-${Date.now()}`,
                                patientId: String(patient.patientId || patient.id),
                                patientName: patient.name,
                                patientPhone: patient.phoneNumber || '',
                                patientEmail: patient.email || '',
                                claim: '',
                                amount: '',
                              }]);
                            } else {
                              setSelectedPatient(patient);
                            }
                            setPatientSearchTerm('');
                            setSearchResults([]);
                          }}
                          style={{
                            padding: '10px',
                            borderBottom: '1px solid #eef0ee',
                            cursor: 'pointer',
                            ':hover': { backgroundColor: '#f7f8f7' }
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f7f8f7'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          <div data-phi style={{ fontWeight: 'bold' }}>{patient.name}</div>
                          <div data-phi style={{ fontSize: '12px', color: '#6b716d' }}>
                            {patient.phoneNumber} {patient.email && `| ${patient.email}`} | ID: {patient.patientId}
                          </div>
                        </div>
                      ))}
                      {searchResults.length === 0 && !isSearching && (
                        <div style={{ padding: '10px', color: '#6b716d', textAlign: 'center' }}>
                          No patients found
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Selected Patient */}
            {selectedPatient && (
              <div style={{
                backgroundColor: '#e6efe9',
                padding: '15px',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                <div style={{ fontWeight: 'bold', color: '#1f5a3d' }}>Selected Patient:</div>
                <div data-phi>{selectedPatient.name}</div>
                <div data-phi style={{ fontSize: '12px', color: '#6b716d' }}>
                  {selectedPatient.phoneNumber} {selectedPatient.email && `| ${selectedPatient.email}`} | ID: {selectedPatient.patientId}
                </div>
              </div>
            )}

            {/* Insurance invoices: the patients this carrier payment covers,
                each with their claim and amount. */}
            {isInsuranceInvoice && (
              <InsuranceAllocations
                practiceId={practiceId}
                rows={insuranceRows}
                onChange={setInsuranceRows}
                autoPostOn={autoPostLedgerEnabled && autoPostInsuranceEnabled}
              />
            )}
            {isInsuranceInvoice && insuranceRows.length > 1 && (
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="pm-inv-payer" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Paid by (optional):
                </label>
                <input
                  id="pm-inv-payer"
                  type="text"
                  placeholder="Insurance carrier, e.g. Delta Dental"
                  value={payerName}
                  onChange={(e) => setPayerName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #e3e7e4',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            )}

            {/* Invoice Details */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Description:
              </label>
              <input
                type="text"
                placeholder="Invoice description..."
                value={invoiceData.description}
                onChange={(e) => setInvoiceData({ ...invoiceData, description: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e3e7e4',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showDueDate}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setShowDueDate(checked);
                    setInvoiceData({ ...invoiceData, dueDate: checked ? getTwoWeeksFromToday() : '' });
                  }}
                  style={{ cursor: 'pointer' }}
                />
                Add a due date
              </label>
              {showDueDate ? (
                <input
                  type="date"
                  value={invoiceData.dueDate}
                  onChange={(e) => setInvoiceData({ ...invoiceData, dueDate: e.target.value })}
                  style={{
                    display: 'block',
                    marginTop: '8px',
                    width: '200px',
                    padding: '8px',
                    border: '1px solid #e3e7e4',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              ) : null}
            </div>

            {/* Line Items — insurance invoices use the patient rows above. */}
            <div style={{ marginBottom: '20px', display: isInsuranceInvoice ? 'none' : undefined }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ fontWeight: 'bold' }}>Line Items:</label>
                <button
                  onClick={addLineItem}
                  style={{
                    backgroundColor: '#1d6b40',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  + Add Item
                </button>
              </div>

              {lineItems.map(item => (
                <div key={item.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 100px 40px',
                  gap: '10px',
                  marginBottom: '10px',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                    style={{
                      padding: '6px',
                      border: '1px solid #e3e7e4',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  />
                  {/* Quantity is intentionally hidden — invoices created
                      here are almost always single-line copays. The qty
                      stays at the default of 1 (set in makeDefaultLineItem
                      / addLineItem) so calculateTotal still works. */}
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Price"
                    value={item.price}
                    onChange={(e) => updateLineItem(item.id, 'price', e.target.value)}
                    style={{
                      padding: '6px',
                      border: '1px solid #e3e7e4',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  />
                  <button
                    onClick={() => removeLineItem(item.id)}
                    style={{
                      backgroundColor: '#b42318',
                      color: 'white',
                      border: 'none',
                      padding: '6px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {/* Total */}
            <div style={{
              backgroundColor: '#f7f8f7',
              padding: '15px',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Total:</span>
                <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#1f5a3d' }}>
                  ${calculateTotal()}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleSaveInvoice()}
                disabled={isSaving}
                style={{
                  backgroundColor: isSaving ? '#cdd3cf' : '#1f5a3d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                {isSaving ? 'Saving...' : 'Save Invoice'}
              </button>
              {/* Create & Charge: create the invoice and immediately open the
                  Send-to-Terminal flow so the patient can pay at the desk.
                  Hidden for surcharging practices (charge-at-desk is scoped to
                  non-surcharging practices only). */}
              {!practicePaymentsSettings?.surchargeEnabled && (
                <button
                  onClick={() => handleSaveInvoice({ chargeNow: true })}
                  disabled={isSaving}
                  style={{
                  backgroundColor: isSaving ? '#cdd3cf' : '#1d6b40',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
                >
                  {isSaving ? 'Saving...' : 'Create & Charge'}
                </button>
              )}
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setIsInsuranceInvoice(false);
                  setSelectedPatient(null);
                  resetInsuranceForm();
                  setPatientSearchTerm('');
                  setSearchResults([]);
                  setLineItems([makeDefaultLineItem()]);
                  setInvoiceData({ patientId: '', description: 'Estimated Copay', dueDate: '' });
                  setShowDueDate(false);
                }}
                style={{
                  backgroundColor: '#6b716d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          (() => {
            // First narrow by the sticky patient search and the type filter,
            // so the tab counts and the rendered list reflect the same set.
            const trimmedFilter = invoiceFilter.trim().toLowerCase();
            // For phone matches, strip everything that isn't a digit on both
            // sides so "5551234" finds "+1 (555) 123-4...".
            const filterDigits = trimmedFilter.replace(/\D/g, '');
            const matchesFilter = (inv) => {
              if (!trimmedFilter) return true;
              const haystackText = [
                inv.patientName,
                inv.patientEmail,
                inv.patientId,
              ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              if (haystackText.includes(trimmedFilter)) return true;
              // Needs 3+ digits so a stray "1" doesn't match every phone.
              if (filterDigits.length >= 3 && inv.patientPhone) {
                const phoneDigits = String(inv.patientPhone).replace(/\D/g, '');
                if (phoneDigits.includes(filterDigits)) return true;
              }
              return false;
            };
            const matchesType = (inv) => {
              if (invoiceTypeFilter === 'insurance') return inv.invoiceType === 'insurance';
              if (invoiceTypeFilter === 'patient') return inv.invoiceType !== 'insurance';
              return true;
            };
            const visibleInvoices = invoices.filter((inv) => matchesFilter(inv) && matchesType(inv)
              && (!attentionOnly || attentionReason(inv)));

            // Bucket by status. `superseded` and `cancelled` are terminal,
            // non-actionable states — never in Open. Anything else that isn't
            // paid/superseded/cancelled (pending, processing, authorized,
            // failed, ...) is work in progress and belongs in Open.
            const pendingInvoices = visibleInvoices.filter(inv => {
              const s = inv.status?.toLowerCase();
              return s !== 'paid' && s !== 'superseded' && s !== 'cancelled';
            });
            const paidInvoices = visibleInvoices.filter(inv => inv.status?.toLowerCase() === 'paid');
            const supersededInvoices = visibleInvoices.filter(inv => inv.status?.toLowerCase() === 'superseded');
            const cancelledInvoices = visibleInvoices.filter(inv => inv.status?.toLowerCase() === 'cancelled');
            // Staff-resolved Needs attention items, whatever their status (they
            // also stay in their status tab). Ignores the Needs attention
            // filter, which by definition excludes them, so the count is right
            // from any view; search and type filters still apply.
            const resolvedInvoices = invoices.filter((inv) => matchesFilter(inv) && matchesType(inv)
              && isAttentionResolved(inv));

            // Open-tab status split. "Processing" includes authorized; other
            // open statuses (failed, returned, ...) only show under All.
            const openAwaiting = pendingInvoices.filter(inv => inv.status?.toLowerCase() === 'pending');
            const openProcessing = pendingInvoices.filter(inv => {
              const s = inv.status?.toLowerCase();
              return s === 'processing' || s === 'authorized';
            });

            let displayedInvoices;
            if (invoiceViewMode === 'all') {
              displayedInvoices = visibleInvoices;
            } else if (invoiceViewMode === 'paid') {
              displayedInvoices = paidInvoices;
            } else if (invoiceViewMode === 'superseded') {
              displayedInvoices = supersededInvoices;
            } else if (invoiceViewMode === 'cancelled') {
              displayedInvoices = cancelledInvoices;
            } else if (invoiceViewMode === 'resolved') {
              displayedInvoices = resolvedInvoices;
            } else if (openStatusFilter === 'pending') {
              displayedInvoices = openAwaiting;
            } else if (openStatusFilter === 'processing') {
              displayedInvoices = openProcessing;
            } else {
              displayedInvoices = pendingInvoices;
            }

            const searchActive = !!trimmedFilter;
            const filtersActive = searchActive
              || attentionOnly
              || invoiceTypeFilter !== 'all'
              || (invoiceViewMode === 'pending' && openStatusFilter !== 'all');
            const clearAllFilters = () => {
              setInvoiceFilter('');
              setInvoiceTypeFilter('all');
              setOpenStatusFilter('all');
              setAttentionOnly(false);
            };

            const pageCount = Math.max(1, Math.ceil(displayedInvoices.length / INVOICE_PAGE_SIZE));
            const page = Math.min(invoicePage, pageCount);
            const pageStart = (page - 1) * INVOICE_PAGE_SIZE;
            const pageInvoices = displayedInvoices.slice(pageStart, pageStart + INVOICE_PAGE_SIZE);

            const viewNoun = {
              all: 'flagged', pending: 'open', paid: 'paid', superseded: 'superseded', cancelled: 'cancelled',
              resolved: 'manually resolved',
            }[invoiceViewMode] || 'open';
            const dateHeading = {
              pending: 'Created', paid: 'Paid', superseded: 'Superseded', cancelled: 'Cancelled',
              resolved: 'Resolved',
            }[invoiceViewMode] || 'Created';
            // Column tracks per tab; every row follows the table's --pm-cols.
            // The last track holds the row actions, which always start with
            // the Ledger button, and widens for the Resolve button when this
            // page has Needs attention rows.
            const actionsExtra = pageInvoices.some((inv) => attentionReason(inv)) ? 96 : 0;

            // "Resolve selected": checkboxes only while the Needs attention
            // filter is on, over every flagged row in this tab (all pages).
            const resolveTarget = attentionOnly
              ? displayedInvoices.filter((inv) => attentionReason(inv)).slice(0, RESOLVE_BULK_MAX)
              : [];
            const bulkResolveMode = resolveTarget.length > 0;
            const selectedForResolve = resolveTarget.filter((inv) => resolveSelected.has(inv.id));
            const allResolveSelected = bulkResolveMode && selectedForResolve.length === resolveTarget.length;
            const someResolveSelected = selectedForResolve.length > 0;
            const selectedResolveTotal = selectedForResolve.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);
            const toggleAllResolve = () => setResolveSelected(
              allResolveSelected ? new Set() : new Set(resolveTarget.map((inv) => inv.id)));
            const toggleResolve = (id) => setResolveSelected((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            });

            const columnsBase = invoiceViewMode === 'paid'
              ? `minmax(190px, 1.5fr) minmax(150px, 1.3fr) minmax(90px, 0.7fr) minmax(150px, 1fr) minmax(100px, 0.8fr) minmax(120px, 0.9fr) ${190 + actionsExtra}px`
              : invoiceViewMode === 'pending'
                ? `minmax(190px, 1.5fr) minmax(150px, 1.4fr) minmax(90px, 0.7fr) minmax(150px, 1fr) minmax(110px, 0.8fr) ${216 + actionsExtra}px`
                : `minmax(190px, 1.5fr) minmax(150px, 1.4fr) minmax(90px, 0.7fr) minmax(150px, 1fr) minmax(110px, 0.8fr) ${272 + actionsExtra}px`;
            const columns = bulkResolveMode ? `28px ${columnsBase}` : columnsBase;

            const reconTitle = (invoice) => (
              invoice.ledgerPost?.status === 'posted'
                ? `Posted to the PMS ledger by Newton${invoice.reconciliationMatch ? ` (txn ${invoice.reconciliationMatch.remote_id})` : ''}`
                : invoice.reconciliationMatch ? `Matched: ${invoice.reconciliationMatch.remote_id}` : ''
            );
            const statusPillStyle = { padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' };
            const reconPillStyle = {
              padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
            };

            return (
              <section className="pm-card" ref={invoiceListRef}>
                {/* Toolbar: status tabs + patient search */}
                <div className="pm-toolbar">
                  <div className="pm-segmented" role="group" aria-label="Invoice status">
                    {[
                      // Every flagged invoice in one list, whatever its status.
                      ...(attentionOnly ? [['all', 'All', visibleInvoices.length]] : []),
                      ['pending', 'Open', pendingInvoices.length],
                      ['paid', 'Paid / Reconcile', paidInvoices.length],
                      ['superseded', 'Superseded', supersededInvoices.length],
                      ['cancelled', 'Cancelled', cancelledInvoices.length],
                      ['resolved', 'Manually resolved', resolvedInvoices.length],
                    ].map(([mode, label, count]) => (
                      <button
                        key={mode}
                        type="button"
                        className="pm-segment"
                        aria-pressed={invoiceViewMode === mode}
                        onClick={() => {
                          // Resolved items are never in Needs attention.
                          if (mode === 'resolved') setAttentionOnly(false);
                          setInvoiceViewMode(mode);
                        }}
                      >
                        {label}
                        <span className="pm-segment-count">{count}</span>
                      </button>
                    ))}
                  </div>
                  <div className="pm-search pm-search-compact pm-toolbar-end" style={{ flex: '0 1 320px', minWidth: 220 }}>
                    <Icon name="search" size={15} />
                    <input
                      type="text"
                      value={invoiceFilter}
                      onChange={(e) => setInvoiceFilter(e.target.value)}
                      placeholder="Filter by patient name, email, or phone"
                      aria-label="Filter invoices by patient"
                    />
                    {invoiceFilter && (
                      <button
                        type="button"
                        className="pm-search-clear"
                        onClick={() => setInvoiceFilter('')}
                        aria-label="Clear filter"
                        title="Clear filter"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Filters: type (all tabs) + status split (Open tab only) */}
                <div className="pm-toolbar">
                  <div className="pm-segmented" role="group" aria-label="Invoice type">
                    {[
                      ['all', 'All types'],
                      ['patient', 'Patient'],
                      ['insurance', 'Insurance'],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        className="pm-segment"
                        aria-pressed={invoiceTypeFilter === id}
                        onClick={() => setInvoiceTypeFilter(id)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {invoiceViewMode === 'pending' && (
                    <div className="pm-segmented" role="group" aria-label="Open invoice status">
                      {[
                        ['all', 'All', pendingInvoices.length],
                        ['pending', 'Pending', openAwaiting.length],
                        ['processing', 'Processing', openProcessing.length],
                      ].map(([id, label, count]) => (
                        <button
                          key={id}
                          type="button"
                          className="pm-segment"
                          aria-pressed={openStatusFilter === id}
                          onClick={() => setOpenStatusFilter(id)}
                        >
                          {label}
                          <span className="pm-segment-count">{count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {attentionOnly && (
                    <button
                      type="button"
                      className="pm-tag pm-tag-danger"
                      style={{ border: 0, cursor: 'pointer' }}
                      onClick={() => setAttentionOnly(false)}
                      title="Remove the Needs attention filter"
                    >
                      Needs attention ✕
                    </button>
                  )}
                  {filtersActive && (
                    <>
                      {/* The count covers search + type + attention (they
                          narrow every tab); the Open status split only picks
                          within a tab. */}
                      <span className="pm-filter-count pm-toolbar-end">
                        {(searchActive || attentionOnly || invoiceTypeFilter !== 'all')
                          ? `Showing ${visibleInvoices.length} of ${invoices.length} invoices`
                          : null}
                      </span>
                      <button type="button" className="pm-btn-primary pm-btn-sm" onClick={clearAllFilters}>
                        Clear filters
                      </button>
                    </>
                  )}
                </div>

                {someResolveSelected && (
                  <div className="pm-toolbar pm-bulkbar">
                    <span className="pm-bulkbar-count">
                      {selectedForResolve.length} selected · {formatCurrency(selectedResolveTotal)}
                    </span>
                    <button
                      type="button"
                      className="pm-btn-primary pm-btn-sm"
                      disabled={isBulkResolving}
                      onClick={() => handleBulkResolve(selectedForResolve)}
                    >
                      <Icon name="check" size={14} />
                      {isBulkResolving ? 'Resolving…' : `Resolve ${selectedForResolve.length}`}
                    </button>
                    <button
                      type="button"
                      className="pm-btn pm-btn-sm"
                      disabled={isBulkResolving}
                      onClick={() => setResolveSelected(new Set())}
                    >
                      Clear selection
                    </button>
                  </div>
                )}

                {isLoadingInvoices ? (
                  <div className="pm-empty">Loading invoices…</div>
                ) : displayedInvoices.length === 0 ? (
                  <div className="pm-empty">
                    <div className="pm-empty-title">
                      {filtersActive
                        ? searchActive && invoiceTypeFilter === 'all' && !(invoiceViewMode === 'pending' && openStatusFilter !== 'all')
                          ? `No ${viewNoun} invoices match "${invoiceFilter}".`
                          : `No ${viewNoun} invoices match the current filters.`
                        : invoiceViewMode === 'pending'
                          ? 'No open invoices. Create one or all invoices have been paid!'
                          : invoiceViewMode === 'superseded'
                            ? 'No superseded invoices.'
                            : invoiceViewMode === 'cancelled'
                              ? 'No cancelled invoices.'
                              : invoiceViewMode === 'resolved'
                                ? 'No manually resolved invoices. Use Resolve on a Needs attention row once you have handled it.'
                                : 'No paid invoices yet.'}
                    </div>
                    {filtersActive && (
                      <button type="button" className="pm-btn-primary pm-btn-sm" style={{ marginTop: 12 }} onClick={clearAllFilters}>
                        Clear filters
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="pm-table-scroll">
                      <div className="pm-table" style={{ '--pm-cols': columns, minWidth: 1040 }}>
                        <div className="pm-row pm-row-head">
                          {bulkResolveMode && (
                            <div>
                              <input
                                type="checkbox"
                                className="pm-checkbox"
                                checked={allResolveSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = someResolveSelected && !allResolveSelected;
                                }}
                                onChange={toggleAllResolve}
                                aria-label={`Select all ${resolveTarget.length} invoices that need attention`}
                                title={`Select all ${resolveTarget.length} invoices that need attention${
                                  resolveTarget.length === RESOLVE_BULK_MAX ? ` (max ${RESOLVE_BULK_MAX} at once)` : ''
                                }`}
                              />
                            </div>
                          )}
                          <div>Patient</div>
                          <div>Description</div>
                          <div className="pm-num">Amount</div>
                          <div>{invoiceViewMode === 'paid' ? 'PMS' : 'Status'}</div>
                          <div>{dateHeading}</div>
                          {invoiceViewMode === 'paid' && <div>Payment method</div>}
                          <div />
                        </div>

                        {pageInvoices.map((invoice) => {
                          const rawReconStyle = getReconciliationStatusStyle(invoice.reconciliationStatus, invoice.status, invoice.ledgerPost);
                          const reconciliationStyle = resolvedReconStyle(invoice, rawReconStyle) || rawReconStyle;
                          const declinedCharge = attentionReason(invoice) === 'declined' ? chargeFailure(invoice) : null;
                          const status = (invoice.status || '').toLowerCase();
                          const isSuperseded = status === 'superseded';
                          const isCancelled = status === 'cancelled';
                          // System-driven cancel (patient paid in the PMS) vs a
                          // staff click — used to surface an "Auto" tag.
                          const isAutoCancelled = isCancelled
                            && (invoice.cancelledBy === 'system' || !!invoice.autoCancelMatch);
                          // Voided rows get muted, struck-through text so the
                          // operator sees at a glance they're not collectible.
                          const isVoid = isSuperseded || isCancelled;
                          const isInsurance = invoice.invoiceType === 'insurance';
                          // Patient ledger (PMS statement) — opened from the
                          // patient name and from the row's Ledger button.
                          const openLedger = (e) => {
                            e.stopPropagation();
                            setStatementPatient({
                              contact_id: invoice.patientId,
                              name: invoice.patientName,
                              phone: invoice.patientPhone || '',
                              email: invoice.patientEmail || '',
                            });
                          };
                          const ledgerButton = invoice.patientId ? (
                            <button
                              type="button"
                              className="pm-btn pm-btn-sm"
                              onClick={openLedger}
                              title={`Open ${invoice.patientName || 'patient'}'s ledger (PMS statement)`}
                            >
                              <Icon name="ledger" size={14} />
                              Ledger
                            </button>
                          ) : null;

                          // Date column: the tab's event date, with context under it.
                          let datePrimary;
                          let dateSub = null;
                          if (invoiceViewMode === 'paid') {
                            datePrimary = formatWhen(invoice.paidAt || invoice.updatedAt);
                            dateSub = invoice.createdAt ? `Created ${formatDate(invoice.createdAt)}` : null;
                          } else if (invoiceViewMode === 'superseded') {
                            datePrimary = formatWhen(invoice.supersededAt || invoice.updatedAt);
                            dateSub = invoice.createdAt ? `Created ${formatDate(invoice.createdAt)}` : null;
                          } else if (invoiceViewMode === 'cancelled') {
                            datePrimary = formatWhen(invoice.cancelledAt || invoice.updatedAt);
                            dateSub = invoice.createdAt ? `Created ${formatDate(invoice.createdAt)}` : null;
                          } else if (invoiceViewMode === 'resolved') {
                            datePrimary = formatWhen(invoice.attentionResolved?.at);
                            dateSub = invoice.attentionResolved?.byEmail ? `By ${invoice.attentionResolved.byEmail}` : null;
                          } else {
                            datePrimary = formatWhen(invoice.createdAt);
                            dateSub = invoice.dueDate ? `Due ${formatDate(invoice.dueDate)}` : null;
                          }

                          // Open-tab actions. Collect only makes sense while the
                          // invoice is still 'pending'; the reasons mirror the
                          // backend guards (e.g. charge_invoice_from_saved_pm).
                          const collectItems = [
                            {
                              id: 'sms',
                              icon: 'message',
                              label: 'Text payment link',
                              disabledReason: isInsurance
                                ? 'Insurance invoices are not sent to patients'
                                : !invoice.patientPhone ? 'No phone number on file for this patient' : null,
                              onSelect: () => {
                                setPendingSmsInvoice({
                                  invoice: invoice,
                                  patient: { phoneNumber: invoice.patientPhone, name: invoice.patientName }
                                });
                                setShowSmsDialog(true);
                              },
                            },
                            {
                              id: 'email',
                              icon: 'mail',
                              label: 'Email payment link',
                              disabledReason: isInsurance
                                ? 'Insurance invoices are not sent to patients'
                                : !invoice.patientEmail ? 'No email on file for this patient' : null,
                              onSelect: () => {
                                setPendingEmailInvoice(invoice);
                                setShowEmailDialog(true);
                              },
                            },
                            {
                              id: 'both',
                              icon: 'send',
                              label: 'Text & email payment link',
                              disabledReason: isInsurance
                                ? 'Insurance invoices are not sent to patients'
                                : !invoice.patientPhone && !invoice.patientEmail
                                  ? 'No phone number or email on file for this patient'
                                  : !invoice.patientPhone
                                    ? 'No phone number on file — use Email payment link'
                                    : !invoice.patientEmail
                                      ? 'No email on file — use Text payment link'
                                      : null,
                              onSelect: () => {
                                setPendingBothInvoice(invoice);
                                setShowBothDialog(true);
                              },
                            },
                            { separator: true },
                            {
                              id: 'charge',
                              icon: 'credit-card',
                              // Opens "Charge Card on File": pick a saved card or
                              // key one in manually ("+ Add card manually").
                              label: 'Virtual terminal / Saved card',
                              disabledReason: isInsurance ? 'Insurance invoices cannot be charged to a card' : null,
                              onSelect: () => openChargeCardModal(invoice),
                            },
                            {
                              id: 'terminal',
                              icon: 'smartphone',
                              // Opens the device modal: push the invoice to a
                              // registered card reader for the patient to tap.
                              label: 'Physical terminal',
                              onSelect: () => openDeviceModal(invoice),
                            },
                          ];
                          const moreItems = [
                            {
                              id: 'open',
                              icon: 'external-link',
                              label: 'Open payment page',
                              onSelect: () => window.open(`${FRONTEND_BASE_URL}/invoice/${invoice.publicHash}`, '_blank'),
                            },
                            {
                              id: 'copy',
                              icon: 'copy',
                              label: 'Copy payment link',
                              onSelect: () => {
                                const url = `${FRONTEND_BASE_URL}/invoice/${invoice.publicHash}`;
                                navigator.clipboard.writeText(url).then(() => {
                                  showToast('Link copied!');
                                  logInvoiceActivity(practiceId, invoice.id, 'link_copied', {});
                                  addActivityToLocalInvoice(invoice.id, 'link_copied', {});
                                }).catch(() => {
                                  showToast('Failed to copy link', 'error');
                                });
                              },
                            },
                            {
                              id: 'print',
                              icon: 'printer',
                              label: 'Print invoice',
                              onSelect: () => printInvoice(invoice),
                            },
                            { separator: true },
                            {
                              id: 'receipt',
                              icon: 'receipt',
                              label: 'Email receipt',
                              disabledReason: receiptBlockReason(invoice),
                              onSelect: () => startReceipt(invoice),
                            },
                          ];

                          return (
                            <div
                              key={invoice.id}
                              className={`pm-row pm-row-body${isSuperseded ? ' pm-row-void' : ''}`}
                              onClick={() => {
                                setTimelineInvoice(invoice);
                                setShowTimelineModal(true);
                              }}
                            >
                              {bulkResolveMode && (
                                // Ticking must not open the row's Timeline.
                                <div className="pm-cell" onClick={(e) => e.stopPropagation()}>
                                  {resolveSelected.has(invoice.id) || attentionReason(invoice) ? (
                                    <input
                                      type="checkbox"
                                      className="pm-checkbox"
                                      checked={resolveSelected.has(invoice.id)}
                                      onChange={() => toggleResolve(invoice.id)}
                                      aria-label={`Select ${invoice.patientName || invoice.id} to resolve`}
                                    />
                                  ) : null}
                                </div>
                              )}
                              {/* Patient */}
                              <div className="pm-cell">
                                <div className="pm-cell-title-line">
                                  {invoice.patientId ? (
                                    <button
                                      type="button"
                                      className="pm-cell-link"
                                      onClick={openLedger}
                                      title="View PMS statement"
                                      data-phi
                                    >
                                      {invoice.patientName}
                                    </button>
                                  ) : (
                                    <span className="pm-cell-title" data-phi>{invoice.patientName}</span>
                                  )}
                                  {isInsurance && <span className="pm-tag pm-tag-warn">Insurance</span>}
                                </div>
                                {invoice.patientId && <div className="pm-cell-sub" data-phi>ID {invoice.patientId}</div>}
                              </div>

                              {/* Description — plan installments show just the
                                  plan tag + a link to the plan drawer; their
                                  generated description only repeats the tag. */}
                              <div className="pm-cell">
                                {invoice.paymentPlanId ? (
                                  <div className="pm-plan-cell">
                                    <span
                                      className={`pm-tag pm-tag-violet${isVoid ? ' pm-strike' : ''}`}
                                      title={invoice.paymentPlanName || undefined}
                                    >
                                      Plan
                                      {invoice.paymentPlanInstallmentNumber && invoice.paymentPlanInstallmentTotal
                                        ? ` · #${invoice.paymentPlanInstallmentNumber} of ${invoice.paymentPlanInstallmentTotal}`
                                        : ''}
                                      {invoice.paymentPlanName ? ` · ${invoice.paymentPlanName}` : ''}
                                    </span>
                                    <button
                                      type="button"
                                      className="pm-inline-link"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPlanDetail(invoice.paymentPlanId);
                                      }}
                                    >
                                      View plan details
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    className={`pm-cell-text-wrap${isVoid ? ' pm-strike' : ''}`}
                                    title={invoice.description || undefined}
                                  >
                                    {invoice.description || 'No description'}
                                  </div>
                                )}
                              </div>

                              {/* Amount — green only once the money is collected */}
                              <div className="pm-cell pm-num">
                                <div className={`pm-amount${isVoid ? ' pm-strike' : status === 'paid' ? ' pm-text-ok' : ''}`}>
                                  {formatCurrency(invoice.total)}
                                </div>
                              </div>

                              {/* Status: invoice status + PMS ledger chip (stacked).
                                  The PMS chip also shows for in-flight payments —
                                  offices post immediately, so staff need to see
                                  right away whether Newton posted it, the
                                  reconciler matched the office's own entry, or
                                  we're still looking. Plain unpaid invoices get
                                  no chip (reconciliationStyle is null). */}
                              <div className="pm-cell">
                                {invoiceViewMode !== 'paid' ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                    <span style={{
                                      ...statusPillStyle,
                                      backgroundColor: getStatusStyle(invoice.status).bg,
                                      color: getStatusStyle(invoice.status).color
                                    }}>
                                      {getStatusStyle(invoice.status).label}
                                    </span>
                                    {declinedCharge && (
                                      <span
                                        className="pm-tag pm-tag-danger"
                                        title={chargeFailureTitle(invoice, declinedCharge)}
                                      >
                                        Declined
                                      </span>
                                    )}
                                    {reconciliationStyle && (
                                      <span
                                        style={{ ...reconPillStyle, backgroundColor: reconciliationStyle.bg, color: reconciliationStyle.color }}
                                        title={reconciliationStyle.title || reconTitle(invoice)}
                                      >
                                        <span>{reconciliationStyle.icon}</span>
                                        <span>{reconciliationStyle.label}</span>
                                      </span>
                                    )}
                                  </div>
                                ) : reconciliationStyle ? (
                                  <span
                                    style={{ ...reconPillStyle, backgroundColor: reconciliationStyle.bg, color: reconciliationStyle.color }}
                                    title={reconciliationStyle.title || reconTitle(invoice)}
                                  >
                                    <span>{reconciliationStyle.icon}</span>
                                    <span>{reconciliationStyle.label}</span>
                                  </span>
                                ) : (
                                  <span style={{ ...reconPillStyle, backgroundColor: '#fdf3e3', color: '#a8520c' }}>
                                    <span>⏳</span>
                                    <span>Checking PMS…</span>
                                  </span>
                                )}
                              </div>

                              {/* Date */}
                              <div className="pm-cell">
                                <div className="pm-cell-text">{datePrimary}</div>
                                {dateSub && <div className="pm-cell-sub">{dateSub}</div>}
                              </div>

                              {/* Payment method (Paid tab) */}
                              {invoiceViewMode === 'paid' && (
                                <div className="pm-cell">
                                  {invoice.paymentCard ? (
                                    <>
                                      <div className="pm-cell-text" style={{ fontWeight: 500 }}>
                                        {invoice.paymentCard.brand_desc || invoice.paymentCard.brand}
                                      </div>
                                      <div className="pm-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        •••• {invoice.paymentCard.last_4}
                                        {invoice.paymentCard.type && (
                                          <span className={`pm-tag ${invoice.paymentCard.type === 'CREDIT' ? 'pm-tag-ok' : 'pm-tag-violet'}`} style={{ fontSize: 10 }}>
                                            {invoice.paymentCard.type}
                                          </span>
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="pm-cell-sub">—</span>
                                  )}
                                </div>
                              )}

                              {/* Actions */}
                              <div className="pm-cell-actions">
                                {attentionReason(invoice) && (
                                  <button
                                    type="button"
                                    className="pm-btn pm-btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResolveAttention(invoice);
                                    }}
                                    title="Mark as resolved: remove from Needs attention once you've handled it"
                                  >
                                    <Icon name="check" size={14} />
                                    Resolve
                                  </button>
                                )}
                                {ledgerButton}
                                {invoiceViewMode === 'paid' || ((invoiceViewMode === 'resolved' || invoiceViewMode === 'all') && status === 'paid') ? (
                                  // Paid rows: the receipt (the invoices that most
                                  // need one live here, not on the Open tab).
                                  <button
                                    type="button"
                                    className="pm-btn pm-btn-sm"
                                    disabled={!!receiptBlockReason(invoice)}
                                    title={receiptBlockReason(invoice) || 'Email a PDF receipt to the patient'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startReceipt(invoice);
                                    }}
                                  >
                                    <Icon name="receipt" size={14} />
                                    Receipt
                                  </button>
                                ) : isCancelled ? (
                                  // Operator-cancelled invoices are dead — no
                                  // pay/send/charge actions. The row still opens
                                  // the Timeline (cancellation banner + audit log).
                                  <>
                                    <span className="pm-cell-sub" style={{ marginTop: 0 }} title="This invoice was cancelled and is no longer collectible.">
                                      ⊘ Cancelled
                                    </span>
                                    {isAutoCancelled && (
                                      <span
                                        className="pm-tag pm-tag-ok"
                                        title="Automatically cancelled — the balance was paid in your PMS (office payment)."
                                      >
                                        Auto
                                      </span>
                                    )}
                                  </>
                                ) : isSuperseded ? (
                                  // Superseded rows are voided — never offer to send
                                  // them again. Only view the dead invoice or jump
                                  // to its replacement.
                                  <>
                                    {invoice.publicHash && (
                                      <button
                                        type="button"
                                        className="pm-btn pm-btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // `?showOriginal=true` is mandatory here —
                                          // without it the backend resolver follows
                                          // `supersededBy` and returns the replacement.
                                          window.open(`${FRONTEND_BASE_URL}/invoice/${invoice.publicHash}?showOriginal=true`, '_blank');
                                        }}
                                        title="Open superseded invoice (read-only)"
                                      >
                                        View
                                      </button>
                                    )}
                                    {invoice.supersededBy && (
                                      <button
                                        type="button"
                                        className="pm-btn pm-btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Open the replacement's public page so staff
                                          // see exactly what the patient sees.
                                          // `showOriginal` stops the API re-resolving
                                          // through any future chain.
                                          const replacement = invoices.find(i => i.id === invoice.supersededBy);
                                          const hash = replacement?.publicHash;
                                          if (hash) {
                                            window.open(`${FRONTEND_BASE_URL}/invoice/${hash}?showOriginal=true`, '_blank');
                                          } else {
                                            showToast('Replacement invoice link not available', 'error');
                                          }
                                        }}
                                        title={`Open replacement invoice ${invoice.supersededBy}`}
                                      >
                                        Replaced by →
                                      </button>
                                    )}
                                  </>
                                ) : invoice.publicHash ? (
                                  // Open invoices: Collect while still pending,
                                  // everything else behind ⋯.
                                  <>
                                    {status === 'pending' && (
                                      <RowMenu
                                        items={collectItems}
                                        triggerClassName="pm-btn-primary pm-btn-sm"
                                        triggerLabel="Collect payment for this invoice"
                                        triggerContent={<>Collect <Icon name="chevron-down" size={13} /></>}
                                      />
                                    )}
                                    <RowMenu
                                      items={moreItems}
                                      triggerClassName="pm-icon-btn pm-icon-btn-sm"
                                      triggerLabel="More actions"
                                      triggerContent={<Icon name="more" size={16} />}
                                    />
                                  </>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="pm-card-footer">
                      <span>
                        Showing {pageStart + 1}–{pageStart + pageInvoices.length} of {displayedInvoices.length}
                      </span>
                      <Pager page={page} pageCount={pageCount} onChange={setInvoicePage} label="Invoice pages" />
                    </div>
                  </>
                )}
              </section>
            );
          })()

        )}
      </div>
      )}

      {/* Duplicate-pending confirmation dialog. Shown when the backend
          returns {conflict: true} from POST /api/payments/invoices —
          mirrors LedgerTab's pattern so staff get the same prompt no
          matter which tab they triggered the create from. "Replace"
          re-runs the create with supersede=true (backend marks the old
          row ``superseded`` and stamps ``supersededBy`` pointing at the
          new id, so the audit trail is preserved). "Keep existing" is
          a no-op — the user can navigate to the existing invoice via
          the inline link. Plan installment invoices are filtered out
          of the backend's dedup query, so this only fires for genuine
          one-off conflicts. */}
      {pendingInvoiceConflict && (
        <div
          onClick={() => !isSaving && setPendingInvoiceConflict(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15,23,42,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: 12,
              width: 'min(440px, 100%)',
              boxShadow: '0 24px 48px rgba(15,23,42,0.3)',
              overflow: 'hidden',
              textAlign: 'left',
            }}
          >
            <div style={{ padding: '20px 24px 16px' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#16241c', marginBottom: 8 }}>
                Pending invoice already exists
              </div>
              <div style={{ fontSize: 13, color: '#3c4540', lineHeight: 1.5 }}>
                There is already a pending invoice for{' '}
                <strong data-phi>
                  {pendingInvoiceConflict.existing.patientName
                    || pendingInvoiceConflict.patient?.name
                    || 'this patient'}
                </strong>
                {typeof pendingInvoiceConflict.existing.total === 'number' && (
                  <> for <strong>{formatCurrency(pendingInvoiceConflict.existing.total)}</strong></>
                )}
                {pendingInvoiceConflict.existing.createdAt && (() => {
                  const d = new Date(pendingInvoiceConflict.existing.createdAt);
                  if (Number.isNaN(d.getTime())) return null;
                  return (
                    <> created on <strong>{d.toLocaleString(undefined, {
                      year: 'numeric', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    })}</strong></>
                  );
                })()}
                .
                {pendingInvoiceConflict.existingCount > 1 && (
                  <> ({pendingInvoiceConflict.existingCount} pending invoices total — all will be replaced.)</>
                )}
                {pendingInvoiceConflict.existing.publicHash && (
                  <div style={{ marginTop: 10 }}>
                    <a
                      href={`${FRONTEND_BASE_URL}/invoice/${pendingInvoiceConflict.existing.publicHash}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 12, fontWeight: 600, color: '#1f4e79',
                        textDecoration: 'none', border: '1px solid #c5d8ea',
                        backgroundColor: '#f3f7fb', borderRadius: 6, padding: '6px 10px',
                      }}
                    >
                      View existing invoice
                      <span aria-hidden>↗</span>
                    </a>
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  Do you want to proceed? The existing pending invoice will be marked as <em>superseded</em> and a new one created in its place.
                </div>
              </div>
            </div>
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #e3e7e4',
              backgroundColor: '#f7f8f7',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}>
              <button
                type="button"
                onClick={() => setPendingInvoiceConflict(null)}
                disabled={isSaving}
                style={{
                  fontSize: 13, padding: '8px 14px', borderRadius: 6,
                  border: '1px solid #d6dbd7', backgroundColor: '#fff',
                  color: '#3c4540', cursor: isSaving ? 'not-allowed' : 'pointer',
                }}
              >
                No, keep existing
              </button>
              <button
                type="button"
                onClick={() => handleSaveInvoice({ supersede: true, chargeNow: !!pendingInvoiceConflict?.chargeNow })}
                disabled={isSaving}
                style={{
                  fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 6,
                  border: '1px solid',
                  borderColor: isSaving ? '#cdd3cf' : '#b42318',
                  backgroundColor: isSaving ? '#e3e7e4' : '#b42318',
                  color: isSaving ? '#6b716d' : '#fff',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                }}
              >
                {isSaving ? 'Replacing…' : 'Yes, replace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS Confirmation Dialog */}
      {showSmsDialog && pendingSmsInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#2b302d' }}>
              Send Invoice via SMS?
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '14px' }}>
              Invoice created successfully! Would you like to send a payment link to <span data-phi>{pendingSmsInvoice.patient?.name}</span>?
            </p>
            
            <div style={{
              backgroundColor: '#f7f8f7',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '13px', color: '#6b716d', marginBottom: '4px' }}>
                Phone number:
              </div>
              <div data-phi style={{ fontSize: '15px', fontWeight: '500', color: '#2b302d' }}>
                {pendingSmsInvoice.patient?.phoneNumber}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowSmsDialog(false);
                  setPendingSmsInvoice(null);
                }}
                disabled={isSendingSms}
                style={{
                  backgroundColor: '#6b716d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isSendingSms ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                No, Skip
              </button>
              <button
                onClick={() => sendInvoiceSms(pendingSmsInvoice.invoice, pendingSmsInvoice.patient?.phoneNumber)}
                disabled={isSendingSms}
                style={{
                  backgroundColor: isSendingSms ? '#cdd3cf' : '#1d6b40',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isSendingSms ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {isSendingSms ? 'Sending...' : 'Yes, Send SMS'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Confirmation Dialog */}
      {showEmailDialog && pendingEmailInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#2b302d' }}>
              Send Invoice via Email?
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '14px' }}>
              Send payment link to <span data-phi>{pendingEmailInvoice.patientName}</span>?
            </p>
            
            <div style={{
              backgroundColor: '#f7f8f7',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '13px', color: '#6b716d', marginBottom: '4px' }}>
                Email address:
              </div>
              <div data-phi style={{ fontSize: '15px', fontWeight: '500', color: '#2b302d' }}>
                {pendingEmailInvoice.patientEmail}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEmailDialog(false);
                  setPendingEmailInvoice(null);
                }}
                disabled={isSendingEmail}
                style={{
                  backgroundColor: '#6b716d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isSendingEmail ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => sendInvoiceEmailHandler(pendingEmailInvoice, pendingEmailInvoice.patientEmail)}
                disabled={isSendingEmail}
                style={{
                  backgroundColor: isSendingEmail ? '#cdd3cf' : '#5b3fa8',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isSendingEmail ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {isSendingEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Text & Email Confirmation Dialog */}
      {showBothDialog && pendingBothInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'left'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#2b302d' }}>
              Send Invoice via Text & Email?
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '14px' }}>
              Send payment link to <span data-phi>{pendingBothInvoice.patientName}</span> by text and email?
            </p>

            <div style={{
              backgroundColor: '#f7f8f7',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              display: 'grid',
              gap: '10px'
            }}>
              <div>
                <div style={{ fontSize: '13px', color: '#6b716d', marginBottom: '4px' }}>
                  Phone number:
                </div>
                <div data-phi style={{ fontSize: '15px', fontWeight: '500', color: '#2b302d' }}>
                  {pendingBothInvoice.patientPhone}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '13px', color: '#6b716d', marginBottom: '4px' }}>
                  Email address:
                </div>
                <div data-phi style={{ fontSize: '15px', fontWeight: '500', color: '#2b302d' }}>
                  {pendingBothInvoice.patientEmail}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBothDialog(false);
                  setPendingBothInvoice(null);
                }}
                disabled={isSendingBoth}
                style={{
                  backgroundColor: '#6b716d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isSendingBoth ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => sendInvoiceBoth(pendingBothInvoice)}
                disabled={isSendingBoth}
                style={{
                  backgroundColor: isSendingBoth ? '#cdd3cf' : '#1f5a3d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isSendingBoth ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {isSendingBoth ? 'Sending...' : 'Send Both'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Confirmation Dialog */}
      {showReceiptDialog && pendingReceiptInvoice && (() => {
        // Show the amount actually charged, not the invoice subtotal, so
        // staff confirm the same figure the patient will see on the PDF.
        // Mirrors the money model in backend/receipt_pdf.py.
        const surcharge = pendingReceiptInvoice.surchargeApplied || {};
        const baseCents = Math.round((Number(pendingReceiptInvoice.total) || 0) * 100);
        const surchargeCents = Math.round(Number(surcharge.surchargeCents) || 0);
        let chargedCents;
        if (typeof pendingReceiptInvoice.actualPayinAmountCents === 'number') {
          chargedCents = Math.round(pendingReceiptInvoice.actualPayinAmountCents);
        } else if (typeof surcharge.totalCents === 'number') {
          chargedCents = Math.round(surcharge.totalCents);
        } else {
          chargedCents = baseCents + surchargeCents;
        }
        const card = pendingReceiptInvoice.paymentCard || {};
        const cardLabel = [
          card.brand_desc || card.brand || '',
          card.last_4 ? `•••• ${card.last_4}` : '',
        ].filter(Boolean).join(' ');

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#2b302d' }}>
                Send Receipt via Email?
              </h3>
              <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '14px' }}>
                Email <span data-phi>{pendingReceiptInvoice.patientName}</span> a PDF receipt for{' '}
                <strong>{formatCurrency(chargedCents / 100)}</strong>
                {cardLabel ? ` charged to ${cardLabel}` : ''}. This confirms a
                payment already received — it does not request another one.
              </p>

              <div style={{
                backgroundColor: '#f7f8f7',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                <label
                  htmlFor="receipt-email"
                  style={{ display: 'block', fontSize: '13px', color: '#6b716d', marginBottom: '4px' }}
                >
                  Email address:
                </label>
                <input
                  id="receipt-email"
                  type="email"
                  value={receiptEmail}
                data-phi
                  onChange={(e) => setReceiptEmail(e.target.value)}
                  disabled={isSendingReceipt}
                  placeholder="patient@example.com"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '8px 10px',
                    fontSize: '15px',
                    color: '#2b302d',
                    border: '1px solid #d6dbd7',
                    borderRadius: '4px'
                  }}
                />
                {!pendingReceiptInvoice.patientEmail && (
                  <div style={{ fontSize: '12px', color: '#a8520c', marginTop: '6px' }}>
                    No email on file for this patient — enter one to send.
                  </div>
                )}
                <div style={{ fontSize: '12px', color: '#6b716d', marginTop: '8px' }}>
                  Attachment: 🧾 receipt-{pendingReceiptInvoice.id}.pdf
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowReceiptDialog(false);
                    setPendingReceiptInvoice(null);
                  }}
                  disabled={isSendingReceipt}
                  style={{
                    backgroundColor: '#6b716d',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: isSendingReceipt ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => sendInvoiceReceiptHandler(
                    pendingReceiptInvoice,
                    receiptEmail.trim()
                  )}
                  disabled={isSendingReceipt || !receiptEmail.trim()}
                  style={{
                    backgroundColor: (isSendingReceipt || !receiptEmail.trim()) ? '#cdd3cf' : '#0d5c4d',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: (isSendingReceipt || !receiptEmail.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {isSendingReceipt ? 'Sending...' : 'Send Receipt'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Charge Card on File Modal */}
      {showChargeCardModal && chargeCardInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#2b302d' }}>
              Charge Card on File
            </h3>

            <div style={{
              backgroundColor: '#f7f8f7',
              padding: '16px',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Patient:</strong> <span data-phi>{chargeCardInvoice.patientName}</span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Description:</strong> {chargeCardInvoice.description || 'N/A'}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f5a3d' }}>
                Total: {formatCurrency(chargeCardInvoice.total)}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#3c4540' }}>
                Saved card:
              </label>
              {showAddCardForm ? (
                /* Inline manual-entry mode. The Rainforest Payment
                   Component is mounted in save-only mode bound to a
                   freshly-minted payment_method_config — staff types
                   the card directly into Rainforest's iframe (we
                   never see the PAN). On success we refresh the
                   saved-cards list and auto-select the new row. */
                <StaffAddCardForm
                  practiceId={practiceId}
                  patientId={chargeCardInvoice.patientId}
                  hasExistingCards={chargeCardMethods.length > 0}
                  onSaved={async (savedRow) => {
                    setShowAddCardForm(false);
                    showToast(
                      `Card saved: ${(savedRow?.card?.brand || 'Card').replace(/_/g, ' ')} •• ${savedRow?.card?.last4 || ''}`
                    );
                    await reloadChargeCardMethods(chargeCardInvoice, {
                      selectVaultId: savedRow?.id,
                    });
                  }}
                  onCancel={() => setShowAddCardForm(false)}
                />
              ) : isLoadingChargeCards ? (
                <div style={{ padding: '12px', color: '#6b716d', textAlign: 'center' }}>
                  Loading saved cards…
                </div>
              ) : chargeCardMethods.length === 0 ? (
                /* Empty-state. Staff has three paths to vault a card:
                     1. Send the public invoice link (still works).
                     2. Send to terminal "+ Add Card" (physical tap).
                     3. Manual entry right here, which is what this
                        button opens. The first two are surfaced
                        elsewhere; here we just surface the in-modal
                        action so staff don't have to leave the flow. */
                <div style={{
                  padding: '14px 16px',
                  backgroundColor: '#fdf3e3',
                  borderRadius: '4px',
                  color: '#a8520c',
                  fontSize: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}>
                  <div>
                    This patient has no cards on file. Add one manually below,
                    or send them the public invoice link so they can save one
                    (we'll prompt them on checkout).
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setChargeCardError(null);
                      setShowAddCardForm(true);
                    }}
                    style={{
                      alignSelf: 'flex-start',
                      backgroundColor: 'white',
                      color: '#1f5a3d',
                      border: '1px solid #1f5a3d',
                      padding: '6px 14px',
                      borderRadius: 4,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    + Add card manually
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {chargeCardMethods.map((m) => {
                    const checked = selectedVaultId === m.id;
                    const brand = (m.card?.brand || 'Card').replace(/_/g, ' ');
                    const last4 = m.card?.last4 ? ` •• ${m.card.last4}` : '';
                    const exp = (m.card?.expirationMonth && m.card?.expirationYear)
                      ? ` (Exp ${String(m.card.expirationMonth).padStart(2, '0')}/${String(m.card.expirationYear).slice(-2)})`
                      : '';
                    const cardType = (m.card?.type || '').toUpperCase();
                    const typeLabel = cardType === 'CREDIT'
                      ? 'Credit'
                      : cardType === 'DEBIT'
                        ? 'Debit'
                        : null;
                    const preview = m.surchargePreview;
                    const formatCents = (cents) => {
                      if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
                      return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(cents / 100);
                    };
                    return (
                      <label
                        key={m.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '10px 12px',
                          border: checked ? '2px solid #1f5a3d' : '1px solid #d6dbd7',
                          backgroundColor: checked ? '#f3f7fb' : 'white',
                          borderRadius: 6,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          name="chargeCardVaultId"
                          value={m.id}
                          checked={checked}
                          onChange={() => setSelectedVaultId(m.id)}
                        />
                        <div style={{ flex: 1, fontSize: 14, minWidth: 0, textAlign: 'left' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                            fontWeight: 500,
                          }}>
                            <span>
                              {brand}{last4}{m.isDefault ? ' · Default' : ''}
                            </span>
                            {typeLabel && (
                              <span style={{
                                fontSize: 11,
                                fontWeight: 500,
                                padding: '1px 7px',
                                borderRadius: 999,
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                                backgroundColor: typeLabel === 'Credit' ? '#fdf3e3' : '#e6f6f3',
                                color: typeLabel === 'Credit' ? '#a8520c' : '#0e7c66',
                                border: `1px solid ${typeLabel === 'Credit' ? '#f3d9ae' : '#b6e5dc'}`,
                              }}>
                                {typeLabel}
                              </span>
                            )}
                          </div>
                          <div style={{
                            color: '#8a908c',
                            fontSize: 12,
                            marginTop: 2,
                          }}>
                            {exp ? exp.replace(/^\s*\(|\)\s*$/g, '') : ''}
                          </div>
                          {/* Surcharge preview line — total appears on
                              the Charge button below, so we only
                              surface the percentage + dollar amount of
                              the surcharge itself here. */}
                          {preview && preview.kind === 'surcharge' && (
                            <div style={{
                              color: '#a8520c',
                              fontSize: 12,
                              marginTop: 4,
                              fontWeight: 500,
                            }}>
                              + {preview.surchargePct}% surcharge ({formatCents(preview.surchargeCents)})
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {/* Always-available "+ Add another card" link when at
                  least one saved card is showing. Hidden in the empty
                  state (the bigger button inside the yellow box covers
                  that case) and while the inline form is open. */}
              {!showAddCardForm && !isLoadingChargeCards && chargeCardMethods.length > 0 && (
                <div style={{ marginTop: 10, textAlign: 'left' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setChargeCardError(null);
                      setShowAddCardForm(true);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      color: '#1f5a3d',
                      border: 'none',
                      padding: 0,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    + Add another card manually
                  </button>
                </div>
              )}
            </div>

            {chargeCardError && (
              <div style={{
                color: '#b42318',
                fontSize: 13,
                marginBottom: 12,
                padding: '8px 12px',
                backgroundColor: '#fef5f4',
                border: '1px solid #f5c6c1',
                borderRadius: 4,
              }}>
                {chargeCardError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowChargeCardModal(false);
                  setChargeCardInvoice(null);
                  setSelectedVaultId('');
                  setChargeCardError(null);
                  setShowAddCardForm(false);
                }}
                disabled={isChargingCard}
                style={{
                  backgroundColor: '#6b716d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: isChargingCard ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleChargeCardOnFile}
                disabled={
                  isChargingCard
                  || showAddCardForm
                  || !selectedVaultId
                  || chargeCardMethods.length === 0
                }
                style={{
                  backgroundColor: (
                    isChargingCard
                    || showAddCardForm
                    || !selectedVaultId
                    || chargeCardMethods.length === 0
                  ) ? '#cdd3cf' : '#1f5a3d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: (
                    isChargingCard
                    || showAddCardForm
                    || !selectedVaultId
                    || chargeCardMethods.length === 0
                  ) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {(() => {
                  if (isChargingCard) return 'Charging…';
                  // Reflect the *actual* amount we're about to put on
                  // the card (base + surcharge for credit, base for
                  // everything else) so staff don't have to mentally
                  // add the surcharge before confirming with a
                  // patient on the phone.
                  const selected = chargeCardMethods.find(c => c.id === selectedVaultId);
                  const preview = selected?.surchargePreview;
                  if (preview && preview.kind === 'surcharge') {
                    const total = new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    }).format(preview.totalCents / 100);
                    return `Charge ${total} (incl. surcharge)`;
                  }
                  return `Charge ${formatCurrency(chargeCardInvoice.total)}`;
                })()}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send to Device Modal */}
      {showDeviceModal && deviceModalInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '620px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#2b302d' }}>
              Send to Payment Terminal
            </h3>
            
            {/* Invoice Summary */}
            <div style={{
              backgroundColor: '#f7f8f7',
              padding: '16px',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Patient:</strong> <span data-phi>{deviceModalInvoice.patientName}</span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Description:</strong> {deviceModalInvoice.description || 'N/A'}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f5a3d' }}>
                Total: {formatCurrency(deviceModalInvoice.total)}
              </div>
            </div>

            {showVirtualTerminal ? (
              <VirtualTerminalForm
                invoice={deviceModalInvoice}
                onCancel={() => setShowVirtualTerminal(false)}
                onPaid={() => {
                  // Hand off to the shared "waiting for payment" modal.
                  // No device id → card-on-file/manual style: the payin
                  // is already submitted and settles via the webhook.
                  setShowVirtualTerminal(false);
                  setShowDeviceModal(false);
                  setSelectedDeviceId('');
                  setDeclaredCardType(null);
                  setSaveCardOnPay(true);
                  setPresentingInvoice(deviceModalInvoice);
                  setPresentingDeviceId(null);
                  setPresentingMode('payin');
                  setPresentingBillingPreview(null);
                  setPresentingDeclaredCardType(null);
                  setShowPresentingModal(true);
                }}
              />
            ) : (
            <>
            {/* Device Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#3c4540' }}>
                Select Device:
              </label>
              {isLoadingDevices ? (
                <div style={{ padding: '12px', color: '#6b716d', textAlign: 'center' }}>
                  Loading devices...
                </div>
              ) : devices.length === 0 ? (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#fdf3e3', 
                  borderRadius: '4px',
                  color: '#a8520c',
                  fontSize: '14px'
                }}>
                  No devices registered. Please register a device in the Device Management tab first.
                </div>
              ) : (
                <select
                  value={selectedDeviceId}
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #d6dbd7',
                    borderRadius: '4px',
                    backgroundColor: 'white'
                  }}
                >
                  {devices.map((device) => (
                    <option key={device.device_registration_id} value={device.device_registration_id}>
                      {device.device_name} ({device.make} {device.model})
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            {/* Credit/Debit picker — only shown when the practice has
                surcharging enabled. The device API has no BIN-lookup
                primitive, so this declaration is the *only* pre-tap
                signal we get; the server uses it to mint a replacement
                payin_config carrying the surcharge (credit) or debit
                billing override (debit) before pushing to the terminal.
                Staff is the disclosure layer here — they should ask
                the patient "credit or debit?" out loud before clicking. */}
            {practicePaymentsSettings?.surchargeEnabled && (() => {
              const baseCents = Math.round(Number(deviceModalInvoice.total || 0) * 100);
              const pctCap = Number(practicePaymentsSettings?.surchargePctCap) || 3.0;
              const surchargeCents = Math.floor((baseCents * pctCap) / 100);
              const totalWithSurchargeCents = baseCents + surchargeCents;
              const fmt = (cents) =>
                `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              const radioRow = (value, label, sublabel) => {
                const selected = declaredCardType === value;
                return (
                  <label
                    key={value}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '10px 12px',
                      border: `1px solid ${selected ? '#1f5a3d' : '#e3e7e4'}`,
                      backgroundColor: selected ? '#e6efe9' : 'white',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1,
                      textAlign: 'left',
                    }}
                  >
                    <input
                      type="radio"
                      name="declaredCardType"
                      value={value}
                      checked={selected}
                      onChange={() => setDeclaredCardType(value)}
                      style={{ marginTop: '3px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', color: '#1a1d1b', fontSize: '14px' }}>
                        {label}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b716d', marginTop: '2px' }}>
                        {sublabel}
                      </div>
                    </div>
                  </label>
                );
              };
              return (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#3c4540' }}>
                    Card Type:
                  </label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {radioRow(
                      'CREDIT',
                      'Credit Card',
                      `Total ${fmt(totalWithSurchargeCents)} (incl. ${pctCap}% surcharge ${fmt(surchargeCents)})`
                    )}
                    {radioRow(
                      'DEBIT',
                      'Debit Card',
                      `Total ${fmt(baseCents)} (no surcharge)`
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b716d', marginTop: '8px' }}>
                    Ask the patient "credit or debit?" before tap. Required to apply the correct billing rule.
                  </div>
                </div>
              );
            })()}

            {/* Save card for next time. Mirrors the public invoice
                page's pre-checked checkbox — the webhook handler
                already keys off ``invoice.saveOnPayPreference``
                regardless of payment source, so a tapped card lands
                in the vault automatically when this is on. Default-on
                because the most common case is a returning patient
                who wants the convenience of one-tap next time. */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '12px 14px',
                  border: `1px solid ${saveCardOnPay && !deviceModalIsInsurance ? '#1f5a3d' : '#e3e7e4'}`,
                  backgroundColor: saveCardOnPay && !deviceModalIsInsurance ? '#e6efe9' : '#f7f8f7',
                  borderRadius: '4px',
                  cursor: deviceModalIsInsurance ? 'not-allowed' : 'pointer',
                  opacity: deviceModalIsInsurance ? 0.55 : 1,
                  textAlign: 'left',
                }}
              >
                <input
                  type="checkbox"
                  checked={saveCardOnPay && !deviceModalIsInsurance}
                  disabled={deviceModalIsInsurance}
                  onChange={(e) => setSaveCardOnPay(e.target.checked)}
                  style={{ marginTop: '3px', cursor: deviceModalIsInsurance ? 'not-allowed' : 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#1a1d1b', fontSize: '14px' }}>
                    Save card for next time
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b716d', marginTop: '3px' }}>
                    {deviceModalIsInsurance
                      ? 'Insurance payment: the insurance company’s card will not be saved. Insurance cards are never added to a patient’s card on file.'
                      : 'Securely store this card with our payment processor so the patient can pay future invoices in one tap.'}
                  </div>
                </div>
              </label>
            </div>

            {/* Action Buttons. The "+ Add Card" entry point used to
                live on the far left of this row alongside Send Payment
                — it presented a payment_method_config so the patient
                could tap to vault their card without paying. It's
                currently hidden per product decision; staff should
                rely on the "Save card for next time" checkbox above
                to vault the card in the same tap as the charge. The
                handler (handleAddCardOnDevice) and waiting-modal
                savecard path are left in place so the button can be
                un-hidden by restoring the JSX block below without any
                other wiring. */}
            {(() => {
              const anyBusy = isSendingToDevice || isSavingCardOnDevice;
              const baseDisabled = anyBusy || !selectedDeviceId || devices.length === 0;
              // Surcharging on means the staff must classify the card
              // before we can present the terminal — disable Send
              // Payment until they pick. Add Card is unaffected (no
              // amount → no surcharge concern).
              const surchargeOn = !!practicePaymentsSettings?.surchargeEnabled;
              const sendPaymentDisabled = baseDisabled || (surchargeOn && !declaredCardType);
              // Dynamic Send Payment label so the staff sees the
              // exact total they're about to push to the terminal.
              const baseCents = Math.round(Number(deviceModalInvoice.total || 0) * 100);
              const pctCap = Number(practicePaymentsSettings?.surchargePctCap) || 3.0;
              const surchargeCents = Math.floor((baseCents * pctCap) / 100);
              const totalCents = declaredCardType === 'CREDIT' ? baseCents + surchargeCents : baseCents;
              const sendPaymentLabel = (() => {
                if (isSendingToDevice) return 'Sending...';
                if (!surchargeOn || !declaredCardType) return 'Send Payment';
                const fmt = (cents) =>
                  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                if (declaredCardType === 'CREDIT' && surchargeCents > 0) {
                  return `Send Payment ${fmt(totalCents)} (incl. surcharge)`;
                }
                return `Send Payment ${fmt(totalCents)}`;
              })();
              // "+ Add Card" button intentionally hidden — see comment
              // above. Cancel + Send Payment cluster on the right
              // where the eye lands last.
              return (
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}>
                  {/* Virtual Terminal (manual card entry) is hidden when the
                      practice has surcharging on — that flow needs the
                      bin-lookup/replacement-config surcharge swap which the
                      manual form doesn't implement, so we don't expose it.
                      Empty spacer keeps Cancel/Send right-aligned. */}
                  {surchargeOn ? (
                    <div />
                  ) : (
                    <button
                      onClick={() => setShowVirtualTerminal(true)}
                      disabled={anyBusy}
                      style={{
                        backgroundColor: '#fff',
                        color: '#1f5a3d',
                        border: '1px solid #1f5a3d',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: anyBusy ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Virtual Terminal
                    </button>
                  )}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => {
                        setShowDeviceModal(false);
                        setDeviceModalInvoice(null);
                        setSelectedDeviceId('');
                        setDeclaredCardType(null);
                        setSaveCardOnPay(true);
                        setShowVirtualTerminal(false);
                      }}
                      disabled={anyBusy}
                      style={{
                        backgroundColor: '#6b716d',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: anyBusy ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendToDevice}
                      disabled={sendPaymentDisabled}
                      title={
                        surchargeOn && !declaredCardType
                          ? 'Select Credit or Debit before sending'
                          : ''
                      }
                      style={{
                        backgroundColor: sendPaymentDisabled ? '#cdd3cf' : '#1d6b40',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: sendPaymentDisabled ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {sendPaymentLabel}
                    </button>
                  </div>
                </div>
              );
            })()}
            </>
            )}
          </div>
        </div>
      )}

      {/* Waiting-for-payment modal. Shared between two flows that both
          settle asynchronously via the payin webhook:
            * Device flow (presentingDeviceId set) — Rainforest pushed
              the invoice to a terminal; staff can cancel the
              presentation while the patient hasn't tapped yet.
            * Saved-card-on-file flow (presentingDeviceId === null) —
              Rainforest accepted the payin against a vaulted card and
              is processing it; there's nothing to cancel (the charge
              is already submitted) so we show a Close button instead. */}
      {showPresentingModal && presentingInvoice && (() => {
        const isCardOnFile = !presentingDeviceId;
        const isSavingCard = presentingMode === 'savecard' && !isCardOnFile;
        const canSimulate = !isCardOnFile && isSandboxRainforestId(presentingDeviceId);
        return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '450px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              fontSize: '60px',
              marginBottom: '20px',
              animation: 'pulse 2s infinite'
            }}>
              💳
            </div>

            <h2 style={{ margin: '0 0 10px 0', color: presentingDecline ? '#b42318' : '#1f5a3d' }}>
              {presentingDecline
                ? 'Card Declined'
                : isCardOnFile
                ? 'Processing Payment'
                : isSavingCard
                  ? 'Waiting for Card'
                  : 'Waiting for Payment'}
            </h2>

            {presentingDecline ? (
              <div role="alert" style={{
                backgroundColor: '#fdecea',
                border: '1px solid #f5c2bd',
                color: '#b42318',
                borderRadius: '8px',
                padding: '12px 14px',
                margin: '0 0 25px 0',
                fontSize: '14px',
                textAlign: 'left',
              }}>
                <strong>Not paid.</strong>{' '}
                {(() => {
                  const d = presentingDecline.details || {};
                  const card = d.cardBrand && d.cardLast4 ? `${d.cardBrand} •• ${d.cardLast4}` : 'The card';
                  const why = d.refusalDescription || d.refusalCode || 'Declined';
                  return `${card} was declined (${why}). Ask for another card before the patient leaves.`;
                })()}
              </div>
            ) : (
            <p style={{ color: '#6b716d', margin: '0 0 25px 0' }}>
              {isCardOnFile
                ? 'Charging the saved card on file. The invoice will update when the charge settles.'
                : isSavingCard
                  ? 'Ask the patient to tap or insert their card on the terminal. The card will be saved on file — no charge will be taken.'
                  : 'Payment is being presented on the terminal'}
            </p>
            )}

            <div style={{
              backgroundColor: '#f7f8f7',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '25px',
              textAlign: 'left'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ color: '#6b716d', fontSize: '13px' }}>Patient</span>
                <div data-phi style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {presentingInvoice.patientName || 'N/A'}
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <span style={{ color: '#6b716d', fontSize: '13px' }}>Description</span>
                <div style={{ fontSize: '14px' }}>
                  {presentingInvoice.description || 'Invoice Payment'}
                </div>
              </div>

              {/* Card-type declaration + surcharge breakdown.
                  Only shown for the device payin flow (not save-card,
                  not card-on-file) and only when the staff actually
                  declared a type pre-tap — gives them the full picture
                  of what's being presented to the patient at the
                  terminal: "we told you Credit, total is $103.00,
                  including $3.00 surcharge". Hidden for save-card and
                  card-on-file flows because those have their own UX. */}
              {!isCardOnFile && !isSavingCard && presentingDeclaredCardType && (() => {
                const preview = presentingBillingPreview;
                const declared = presentingDeclaredCardType;
                const isSurcharge = preview && preview.kind === 'surcharge';
                const isDebitOverride = preview && preview.kind === 'debit_billing_override';
                const fmt = (cents) =>
                  `$${(Number(cents) / 100).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`;
                return (
                  <div style={{
                    borderTop: '1px solid #e3e7e4',
                    paddingTop: '12px',
                    marginTop: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#6b716d', fontSize: '13px' }}>Card Type</span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: declared === 'CREDIT' ? '#1f5a3d' : '#1d6b40',
                        backgroundColor: declared === 'CREDIT' ? '#e6efe9' : '#e7f4ec',
                        padding: '3px 10px',
                        borderRadius: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                      }}>
                        {declared === 'CREDIT' ? 'Credit' : 'Debit'}
                      </span>
                    </div>
                    {isSurcharge && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#6b716d' }}>
                        <span>Subtotal</span>
                        <span>{fmt(preview.baseCents)}</span>
                      </div>
                    )}
                    {isSurcharge && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#a8520c' }}>
                        <span>Surcharge ({preview.surchargePct}%)</span>
                        <span>+ {fmt(preview.surchargeCents)}</span>
                      </div>
                    )}
                    {isDebitOverride && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#6b716d', fontStyle: 'italic' }}>
                        <span>Patient pays base — no surcharge applied</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{
                borderTop: '1px solid #e3e7e4',
                paddingTop: '12px',
                marginTop: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: '#6b716d', fontSize: '13px' }}>
                  {isSavingCard ? 'Charge' : 'Amount'}
                </span>
                <span style={{ fontWeight: 'bold', fontSize: isSavingCard ? '16px' : '24px', color: isSavingCard ? '#6b716d' : '#1d6b40' }}>
                  {(() => {
                    if (isSavingCard) return 'No charge — saving card on file';
                    // Prefer the server-confirmed total from the
                    // billing preview when present — that's the actual
                    // amount sitting on the terminal right now (base +
                    // surcharge for credit, base for debit). Falls
                    // back to invoice total when there's no preview.
                    const previewTotal = presentingBillingPreview?.totalCents;
                    if (typeof previewTotal === 'number') {
                      return `$${(previewTotal / 100).toFixed(2)}`;
                    }
                    return `$${(presentingInvoice.total || 0).toFixed(2)}`;
                  })()}
                </span>
              </div>
            </div>

            {/* Sandbox-only Simulate Tap. Lets QA fire the virtual
                terminal interaction from this modal directly — no need
                to alt-tab to the Device Management page. The dropdown
                only appears when the presented device has a `sbx_`
                prefix; production terminals never see it. */}
            {canSimulate && (
              <div style={{
                backgroundColor: '#fdf3e3',
                border: '1px solid #f3d9ae',
                borderRadius: '8px',
                padding: '12px 14px',
                marginBottom: '15px',
                textAlign: 'left',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  marginBottom: '6px',
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#a8520c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Sandbox · Simulate Tap
                  </span>
                  {isSimulatingPresentation && (
                    <span style={{ fontSize: '12px', color: '#a8520c' }}>Sending…</span>
                  )}
                </div>
                <select
                  onChange={(e) => {
                    const value = e.target.value;
                    e.target.value = '';
                    if (value) handleSimulatePresentation(value);
                  }}
                  disabled={isSimulatingPresentation}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    backgroundColor: 'white',
                    border: '1px solid #f3d9ae',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: isSimulatingPresentation ? 'not-allowed' : 'pointer',
                    color: '#a8520c',
                  }}
                >
                  <option value="">Pick a card / outcome…</option>
                  {SIMULATE_DEVICE_PRESETS.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '11px', color: '#a8520c', margin: '6px 0 0 0' }}>
                  Triggers NewtonPay's sandbox simulate endpoint. The modal will close
                  automatically when the resulting webhook lands.
                </p>
              </div>
            )}

            {/* Saved-card flow: the payin is already submitted, so
                there's nothing to "cancel" — clicking Close just hides
                this modal. The polling loop above will still auto-close
                when the status webhook lands. Device flow keeps the
                Cancel-presentation behaviour because Rainforest exposes
                an explicit cancel endpoint while the terminal is
                waiting. */}
            {isCardOnFile ? (
              <button
                onClick={() => {
                  setShowPresentingModal(false);
                  setPresentingInvoice(null);
                  setPresentingDeviceId(null);
                  setPresentingMode('payin');
                }}
                style={{
                  backgroundColor: '#1f5a3d',
                  color: 'white',
                  border: 'none',
                  padding: '14px 40px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                Close
              </button>
            ) : (
              <button
                onClick={handleCancelPresentation}
                disabled={isCancellingPresentation}
                style={{
                  backgroundColor: isCancellingPresentation ? '#cdd3cf' : '#b42318',
                  color: 'white',
                  border: 'none',
                  padding: '14px 40px',
                  borderRadius: '6px',
                  cursor: isCancellingPresentation ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                {isCancellingPresentation
                  ? 'Cancelling...'
                  : isSavingCard
                    ? 'Cancel'
                    : 'Cancel Payment'}
              </button>
            )}

            <p style={{ color: '#6b716d', fontSize: '12px', marginTop: '15px', marginBottom: 0 }}>
              {presentingDecline
                ? 'The invoice stays open so you can charge it again.'
                : isCardOnFile
                ? "We'll update the invoice automatically when the charge settles. You can safely close this dialog."
                : isSavingCard
                  ? "We'll add the card to this patient's saved cards as soon as they tap. Click cancel to abort."
                  : 'Customer can complete payment on the terminal, or click above to cancel'}
            </p>
          </div>
        </div>
        );
      })()}

      {planDetail && (
        <PlanDetailDrawer
          practiceId={practiceId}
          plan={planDetail.plan}
          invoices={planDetail.invoices}
          onClose={() => setPlanDetail(null)}
          onChanged={async () => {
            // Plan actions (cancel, charge now, …) change installment
            // invoices too, so refresh both the drawer and the list.
            await openPlanDetail(planDetail.plan.id);
            await loadInvoices();
          }}
          showToast={showToast}
        />
      )}

      {/* Patient PMS statement — reuses the Ledger tab's read-only patient
          ledger modal, opened straight to the clicked patient. */}
      {statementPatient && (
        <LedgerTab
          modalOnly
          practiceId={practiceId}
          initialPatient={statementPatient}
          newtonReferences={newtonReferences}
          liveLedger={statementLiveLedger}
          onClose={() => {
            setStatementPatient(null);
            setStatementLiveLedger(null);
          }}
        />
      )}

      {/* Invoice Timeline Modal */}
      {showTimelineModal && timelineInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#2b302d', fontSize: '18px' }}>
                Invoice Timeline
              </h3>
              <button
                onClick={() => {
                  setShowTimelineModal(false);
                  setTimelineInvoice(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b716d',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{
              backgroundColor: '#f7f8f7',
              padding: '12px',
              paddingLeft: '28px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div data-phi style={{ fontWeight: '600', color: '#2b302d' }}>{timelineInvoice.patientName}</div>
              {timelineInvoice.patientPhone && (
                <div data-phi style={{ fontSize: '12px', color: '#6b716d', marginTop: '2px' }}>
                  {timelineInvoice.patientPhone}
                </div>
              )}
              {timelineInvoice.patientEmail && (
                <div data-phi style={{ fontSize: '12px', color: '#6b716d', marginTop: '2px' }}>
                  {timelineInvoice.patientEmail}
                </div>
              )}
              <div style={{ fontSize: '13px', color: '#6b716d', marginTop: '4px' }}>
                {formatCurrency(timelineInvoice.total)} • {timelineInvoice.description || 'No description'}
              </div>
            </div>

            {/* Superseded banner — shown on the dead invoice. Mirrors the
                "Payment Completed" green banner below. Includes a button
                to jump straight to the replacement invoice's timeline so
                operators can investigate the latest doc. */}
            {timelineInvoice.status?.toLowerCase() === 'superseded' && (
              <div style={{
                backgroundColor: '#eef0ee',
                border: '1px solid #d6dbd7',
                padding: '12px',
                paddingLeft: '28px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#3c4540',
                  marginBottom: '8px'
                }}>
                  ⊘ Invoice Superseded
                </div>
                <div style={{ fontSize: '12px', color: '#3c4540' }}>
                  {timelineInvoice.supersededAt && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Replaced on:</strong> {formatTimestampInTimezone(timelineInvoice.supersededAt)}
                    </div>
                  )}
                  {timelineInvoice.supersededBy && (
                    <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span><strong>Replaced by:</strong> <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{timelineInvoice.supersededBy}</span></span>
                      {(() => {
                        // Open the actual replacement invoice in the
                        // public preview view (new tab) so staff see
                        // the same UI the patient gets. We pass
                        // `showOriginal=true` so the backend doesn't
                        // try to re-resolve the hash — staff explicitly
                        // asked for this exact invoice.
                        const replacement = invoices.find(i => i.id === timelineInvoice.supersededBy);
                        const hash = replacement?.publicHash;
                        if (!hash) return null;
                        return (
                          <a
                            href={`${FRONTEND_BASE_URL}/invoice/${hash}?showOriginal=true`}
                            target="_blank"
                            rel="noreferrer noopener"
                            style={{
                              backgroundColor: '#1f5a3d',
                              color: 'white',
                              border: 'none',
                              padding: '3px 10px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '500',
                              textDecoration: 'none',
                              display: 'inline-block'
                            }}
                          >
                            Open replacement →
                          </a>
                        );
                      })()}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: '#6b716d', marginTop: '4px' }}>
                    This invoice is no longer collectible. The patient should pay the replacement above.
                  </div>
                </div>
              </div>
            )}

            {/* Cancelled banner — shown on invoices the operator
                explicitly voided via the Cancel Invoice button below.
                Mirrors the Superseded banner above. Includes the
                operator-supplied reason (if any) so future staff can
                see why the invoice was killed. */}
            {timelineInvoice.status?.toLowerCase() === 'cancelled' && (
              <div style={{
                backgroundColor: '#fdecea',
                border: '1px solid #f5c6c1',
                padding: '12px',
                paddingLeft: '28px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#912018',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  ⊘ Invoice Cancelled
                  {(timelineInvoice.cancelledBy === 'system' || timelineInvoice.autoCancelMatch) && (
                    <span
                      title="Automatically cancelled — the balance was paid in your PMS (office payment)."
                      style={{
                        fontSize: '10px', fontWeight: 700, color: '#1d6b40',
                        backgroundColor: '#e7f4ec', border: '1px solid #c6e3d0',
                        borderRadius: '4px', padding: '1px 6px',
                      }}
                    >
                      AUTO-CANCELLED
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#912018' }}>
                  {timelineInvoice.cancelledAt && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Cancelled on:</strong> {formatTimestampInTimezone(timelineInvoice.cancelledAt)}
                    </div>
                  )}
                  {timelineInvoice.cancelReason && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Reason:</strong> {timelineInvoice.cancelReason}
                    </div>
                  )}
                  {timelineInvoice.autoCancelMatch && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Matched PMS payment:</strong>{' '}
                      {formatCurrency(Math.abs(Number(timelineInvoice.autoCancelMatch.amount || 0)))}
                      {timelineInvoice.autoCancelMatch.transaction_date
                        ? ` on ${timelineInvoice.autoCancelMatch.transaction_date}`
                        : ''}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: '#b42318', marginTop: '4px' }}>
                    This invoice is no longer collectible and has been removed from Pending Payments.
                  </div>
                </div>
              </div>
            )}

            {/* "Replaces" banner — shown on the new invoice that took the
                place of one or more pending invoices. Lets the operator
                trace history backwards. */}
            {Array.isArray(timelineInvoice.supersedes) && timelineInvoice.supersedes.length > 0 && (
              <div style={{
                backgroundColor: '#fdf3e3',
                border: '1px solid #f3d9ae',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#a8520c',
                  marginBottom: '10px'
                }}>
                  ↺ Replaces {timelineInvoice.supersedes.length} earlier invoice
                  {timelineInvoice.supersedes.length === 1 ? '' : 's'}
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  {timelineInvoice.supersedes.map((oldId) => {
                    // Open the historical (superseded) invoice in
                    // public preview mode so staff see exactly what was
                    // originally sent to the patient. `showOriginal=true`
                    // is mandatory here — without it the backend would
                    // resolve the dead hash back to this same
                    // replacement invoice and the link would loop.
                    const old = invoices.find(i => i.id === oldId);
                    const hash = old?.publicHash;
                    return (
                      <div
                        key={oldId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                          padding: '6px 10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                          border: '1px solid #f3d9ae',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#a8520c',
                          flexWrap: 'wrap'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          minWidth: 0,
                          flex: '1 1 auto'
                        }}>
                          <span style={{ fontWeight: 600 }}>Invoice</span>
                          <span style={{
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}>
                            {oldId}
                          </span>
                          {old?.total != null && (
                            <span style={{
                              padding: '1px 6px',
                              borderRadius: '4px',
                              backgroundColor: '#fdf3e3',
                              fontSize: '11px',
                              fontWeight: 500,
                              whiteSpace: 'nowrap'
                            }}>
                              {formatCurrency(old.total)}
                            </span>
                          )}
                        </div>
                        {hash ? (
                          <a
                            href={`${FRONTEND_BASE_URL}/invoice/${hash}?showOriginal=true`}
                            target="_blank"
                            rel="noreferrer noopener"
                            style={{
                              backgroundColor: '#a8520c',
                              color: 'white',
                              border: 'none',
                              padding: '3px 10px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 500,
                              textDecoration: 'none',
                              whiteSpace: 'nowrap',
                              flex: '0 0 auto'
                            }}
                          >
                            Open original →
                          </a>
                        ) : (
                          <span style={{
                            fontSize: '11px',
                            color: '#a8520c',
                            fontStyle: 'italic',
                            whiteSpace: 'nowrap',
                            flex: '0 0 auto'
                          }}>
                            link unavailable
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Payment Details - only for paid invoices */}
            {timelineInvoice.status?.toLowerCase() === 'paid' && (
              <div style={{
                backgroundColor: '#e7f4ec',
                border: '1px solid #c6e3d0',
                padding: '12px',
                paddingLeft: '28px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: '13px',
                  color: '#1d6b40',
                  marginBottom: '8px'
                }}>
                  ✓ Payment Completed
                </div>
                <div style={{ fontSize: '12px', color: '#1d6b40' }}>
                  {timelineInvoice.paidAt && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Paid:</strong> {formatTimestampInTimezone(timelineInvoice.paidAt)}
                    </div>
                  )}
                  {timelineInvoice.paymentCard && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Method:</strong> 💳 {timelineInvoice.paymentCard.brand_desc || timelineInvoice.paymentCard.brand} •••• {timelineInvoice.paymentCard.last_4}
                      {timelineInvoice.paymentCard.type && ` (${timelineInvoice.paymentCard.type})`}
                    </div>
                  )}
                  {timelineInvoice.authCode && (
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Auth Code:</strong> {timelineInvoice.authCode}
                    </div>
                  )}
                  {timelineInvoice.payinId && (
                    <div>
                      <strong>Transaction ID:</strong> <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{timelineInvoice.payinId}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Multi-patient insurance invoice: each patient's share and
                whether it posted to the PMS as an insurance payment. */}
            {Array.isArray(timelineInvoice.allocations) && timelineInvoice.allocations.length > 0 && (
              <div style={{
                border: '1px solid #e3e7e4',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ fontWeight: '600', fontSize: '13px', color: '#2b302d', marginBottom: '8px' }}>
                  Patients on this payment ({timelineInvoice.allocations.length})
                  {timelineInvoice.payerName ? ` · paid by ${timelineInvoice.payerName}` : ''}
                </div>
                {timelineInvoice.allocations.map((a) => {
                  const lp = a.ledgerPost || {};
                  const status = lp.status === 'posted'
                    ? { text: 'Posted to PMS', color: '#1d6b40' }
                    : lp.status === 'failed'
                      ? { text: 'PMS post failed — post it in the PMS', color: '#b42318' }
                      : lp.status === 'skipped'
                        ? { text: ({
                            no_claim: 'No claim — post it in the PMS',
                            estimate_mismatch: `Differs from the claim estimate${lp.estimate != null ? ` ($${Number(lp.estimate).toFixed(2)})` : ''} — post it in the PMS from the EOB`,
                            claim_already_paid: 'Claim already has an insurance payment — post it in the PMS',
                            no_claim_lines: 'No procedures left to pay on this claim — post it in the PMS',
                          })[lp.reason] || 'Claim not found — post it in the PMS', color: '#a8520c' }
                        : { text: 'Not posted yet', color: '#6b716d' };
                  const claim = a.claimSummary;
                  return (
                    <div key={a.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '6px 0',
                      borderTop: '1px solid #eef0ee',
                      fontSize: '12px'
                    }}>
                      <div>
                        <div data-phi style={{ fontWeight: 600, color: '#2b302d' }}>{a.patientName}</div>
                        <div style={{ color: '#6b716d' }}>
                          {claim
                            ? [claim.carrier, claim.claimTime ? formatDate(claim.claimTime) : null, `claim ${claim.remoteId}`].filter(Boolean).join(' · ')
                            : 'No claim linked'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 600 }}>${Number(a.amount || 0).toFixed(2)}</div>
                        <div style={{ color: status.color }}>{status.text}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* PMS Reconciliation Status — paid + in-flight (processing)
                payments, and any invoice Newton already posted to the ledger
                (shown immediately, pre-settlement). */}
            {(['paid', 'processing'].includes(timelineInvoice.status?.toLowerCase())
              || timelineInvoice.ledgerPost?.status === 'posted') && (
              <div style={{
                backgroundColor: getReconciliationStatusStyle(timelineInvoice.reconciliationStatus, timelineInvoice.status, timelineInvoice.ledgerPost)?.bg || '#f7f8f7',
                border: `1px solid ${getReconciliationStatusStyle(timelineInvoice.reconciliationStatus, timelineInvoice.status, timelineInvoice.ledgerPost)?.color || '#e3e7e4'}20`,
                padding: '12px',
                paddingLeft: '28px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: '13px',
                  color: getReconciliationStatusStyle(timelineInvoice.reconciliationStatus, timelineInvoice.status, timelineInvoice.ledgerPost)?.color || '#6b716d',
                  marginBottom: '8px'
                }}>
                  {getReconciliationStatusStyle(timelineInvoice.reconciliationStatus, timelineInvoice.status, timelineInvoice.ledgerPost)?.icon} PMS Reconciliation: {getReconciliationStatusStyle(timelineInvoice.reconciliationStatus, timelineInvoice.status, timelineInvoice.ledgerPost)?.label}
                </div>

                {/* Newton auto-posted this payment to the PMS ledger — surface
                    when + the Dentrix txn id so staff don't post it again. */}
                {timelineInvoice.ledgerPost?.status === 'posted' && (
                  <div style={{ fontSize: '12px', color: '#1d6b40', marginBottom: '8px' }}>
                    Newton posted this payment to the PMS ledger
                    {timelineInvoice.ledgerPost.postedAt ? ` on ${formatDate(timelineInvoice.ledgerPost.postedAt)}` : ''}
                    {(timelineInvoice.reconciliationMatch?.remote_id || timelineInvoice.ledgerPost.zatannaPaymentId)
                      ? ` — transaction ${timelineInvoice.reconciliationMatch?.remote_id || timelineInvoice.ledgerPost.zatannaPaymentId}`
                      : ''}.
                  </div>
                )}

                {/* What Newton actually wrote to the ledger — provider,
                    payment type, and the Dentrix reference — resolved from
                    the ``ledgerPost`` stamp so staff can verify attribution. */}
                {timelineInvoice.ledgerPost?.status === 'posted' && (() => {
                  const lp = timelineInvoice.ledgerPost;
                  const provId = lp.providerId != null ? String(lp.providerId) : null;
                  const provName = provId ? ledgerProviderNameById[provId] : null;
                  const ptId = lp.paymentTypeId != null ? String(lp.paymentTypeId) : null;
                  const ptName = ptId ? ledgerPaymentTypeNameById[ptId] : null;
                  const sourceLabel = {
                    last_activity: 'from last visit',
                    contact_default: "patient's default provider",
                    admin_default: 'practice default provider',
                    practice_any: 'practice fallback',
                  }[lp.providerSource];
                  const splits = Array.isArray(lp.splits) ? lp.splits : [];
                  const isSplit = lp.state === 'split' && splits.length > 0;
                  const provLabel = (id) => {
                    const s = id != null ? String(id) : null;
                    if (!s) return '—';
                    const n = ledgerProviderNameById[s];
                    return n ? `${n} (${s})` : s;
                  };
                  const hasAny = provId || ptId || lp.reference || lp.guarantorId != null || lp.paymentDate || isSplit;
                  if (!hasAny) return null;
                  return (
                    <div style={{ fontSize: '12px', color: '#6b716d', marginBottom: '8px' }}>
                      {isSplit && (
                        <div
                          style={{
                            marginBottom: '8px',
                            padding: '8px 10px',
                            background: '#e7f4ec',
                            border: '1px solid #1d6b40',
                            borderRadius: '6px',
                            color: '#1d6b40',
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                            ⑃ Broken down into {splits.length} providers
                          </div>
                          {lp.original && (
                            <div style={{ color: '#1d6b40', marginBottom: '4px' }}>
                              Was {provLabel(lp.original.providerId)} ·{' '}
                              {formatCurrency(lp.original.amount)}
                            </div>
                          )}
                          {splits.map((sp, i) => (
                            <div key={i} style={{ marginBottom: '2px' }}>
                              • {provLabel(sp.providerId)} — {formatCurrency(sp.amount)}
                            </div>
                          ))}
                          {(lp.history || []).filter((h) => h.action === 'split').slice(-1).map((h, i) => (
                            h.at || h.by?.email ? (
                              <div key={i} style={{ color: '#8a908c', marginTop: '4px' }}>
                                {h.at ? formatDate(h.at) : ''}
                                {h.by?.email ? ` · by ${h.by.email}` : ''}
                              </div>
                            ) : null
                          ))}
                        </div>
                      )}
                      {!isSplit && provId && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Provider:</strong>{' '}
                          {provName ? `${provName} (${provId})` : provId}
                          {sourceLabel && (
                            <span style={{ color: '#8a908c' }}> · {sourceLabel}</span>
                          )}
                        </div>
                      )}
                      {ptId && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Payment type:</strong>{' '}
                          {ptName ? `${ptName} (${ptId})` : ptId}
                        </div>
                      )}
                      {lp.reference && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Reference:</strong>{' '}
                          <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{lp.reference}</span>
                        </div>
                      )}
                      {lp.guarantorId != null && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Guarantor ID:</strong> {lp.guarantorId}
                        </div>
                      )}
                      {lp.paymentDate && (
                        <div>
                          <strong>Ledger date:</strong> {formatDate(lp.paymentDate)}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {timelineInvoice.reconciliationMatch && (
                  <div style={{ fontSize: '12px', color: '#6b716d' }}>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Transaction ID:</strong> {timelineInvoice.reconciliationMatch.remote_id}
                    </div>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Posted:</strong> {formatDate(timelineInvoice.reconciliationMatch.transaction_time)}
                    </div>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Amount:</strong> {formatCurrency(timelineInvoice.reconciliationMatch.amount)}
                    </div>
                    <div>
                      <strong>Match Type:</strong> {timelineInvoice.reconciliationMatch.matchType === 'auto' ? 'Automatic' : 'Manual'}
                    </div>
                  </div>
                )}
                
                {timelineInvoice.reconciliationMismatch && (
                  <div style={{ fontSize: '12px', color: '#9a3412' }}>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Expected:</strong> {formatCurrency(timelineInvoice.reconciliationMismatch.expectedAmount)}
                    </div>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>In PMS:</strong> {formatCurrency(timelineInvoice.reconciliationMismatch.actualAmount)}
                    </div>
                    <div>
                      <strong>Difference:</strong> {formatCurrency(timelineInvoice.reconciliationMismatch.difference)}
                    </div>
                  </div>
                )}
                
                {timelineInvoice.reconciliationStatus === 'not_found' && timelineInvoice.reconciliationCheckedAt && (
                  <div style={{ fontSize: '11px', color: '#6b716d', marginTop: '4px' }}>
                    Last checked: {formatTimestampInTimezone(timelineInvoice.reconciliationCheckedAt)}
                  </div>
                )}
                
                {(!timelineInvoice.reconciliationStatus || timelineInvoice.reconciliationStatus === 'pending')
                  && timelineInvoice.ledgerPost?.status !== 'posted' && (
                  <div style={{ fontSize: '11px', color: '#a8520c' }}>
                    Payment will be matched with PMS transactions automatically within 4 days.
                  </div>
                )}
              </div>
            )}

            <div style={{ position: 'relative', paddingLeft: '0' }}>
              {(() => {
                // Build timeline: always start with invoice_created from createdAt
                const activityLog = timelineInvoice.activityLog || [];
                const hasCreatedEntry = activityLog.some(a => a.action === 'invoice_created');
                const hasProcessingEntry = activityLog.some(a => a.action === 'payment_processing');
                const hasPaidEntry = activityLog.some(a => a.action === 'payment_completed');
                const hasFailedEntry = activityLog.some(a => a.action === 'payment_failed');
                const status = timelineInvoice.status?.toLowerCase();
                
                let timeline = hasCreatedEntry ? [...activityLog] : [
                  { action: 'invoice_created', timestamp: timelineInvoice.createdAt, details: {} },
                  ...activityLog
                ];
                
                // Add payment_processing if invoice is processing and not already in log.
                //
                // Timestamp source: the real processing time is the
                // ``payin.processing`` webhook (its received_at, or the
                // payin's own updated_at). ``processingAt`` is never
                // stamped on the doc, and falling back to ``updatedAt``
                // is wrong — that reflects the LAST write (usually the
                // much-later success / PMS-reconcile), which sorts this
                // entry to the bottom of the timeline with a bogus date.
                if ((status === 'processing' || status === 'paid') && !hasProcessingEntry) {
                  const payinWebhooks = Array.isArray(timelineInvoice.payinWebhooks)
                    ? timelineInvoice.payinWebhooks
                    : [];
                  const processingHook = payinWebhooks.find(w => w.event_type === 'payin.processing');
                  const processingTs = timelineInvoice.processingAt
                    || (processingHook && (processingHook?.data?.data?.updated_at || processingHook.received_at))
                    || timelineInvoice.updatedAt;
                  timeline.push({
                    action: 'payment_processing',
                    timestamp: processingTs,
                    details: {}
                  });
                }
                
                // Add payment_completed at end if invoice is paid and not already in log
                if (status === 'paid' && !hasPaidEntry) {
                  timeline.push({
                    action: 'payment_completed',
                    timestamp: timelineInvoice.paidAt || timelineInvoice.updatedAt,
                    details: {}
                  });
                }

                // Surface a declined charge. The backend stamps a
                // payment_failed activityLog entry on the payin.failed
                // webhook, but older invoices (declined before that
                // change) only carry the refusal fields — synthesize the
                // event from them so the timeline still shows the decline.
                if (!hasFailedEntry && (timelineInvoice.refusalCode || timelineInvoice.failedAt)) {
                  timeline.push({
                    action: 'payment_failed',
                    timestamp: timelineInvoice.failedAt || timelineInvoice.updatedAt,
                    details: {
                      refusalCode: timelineInvoice.refusalCode,
                      refusalDescription: timelineInvoice.refusalDescription,
                    }
                  });
                }
                
                // Sort timeline by timestamp
                timeline.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                
                return timeline.map((activity, index, arr) => {
                  const isGreen = activity.action === 'invoice_created' || activity.action === 'payment_completed' || activity.action === 'ledger_posted';
                  const isYellow = activity.action === 'payment_processing';
                  const isRed = activity.action === 'payment_failed' || activity.action === 'ledger_post_failed';
                  const isGray = activity.action === 'invoice_superseded' || activity.action === 'invoice_cancelled';
                  const dotColor = isGray
                    ? '#6b716d'
                    : isRed
                      ? '#b42318'
                      : isGreen
                        ? '#1d6b40'
                        : isYellow
                          ? '#d9901a'
                          : '#1f5a3d';
                  
                  return (
                  <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '16px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: dotColor,
                        border: '2px solid white',
                        boxShadow: '0 0 0 2px ' + dotColor
                      }} />
                      {index < arr.length - 1 && (
                        <div style={{
                          width: '2px',
                          height: '40px',
                          backgroundColor: '#e3e7e4',
                          margin: '4px 0'
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1, paddingBottom: index < arr.length - 1 ? '24px' : '0' }}>
                      <div style={{ fontWeight: '600', color: '#2b302d', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {getActionLabel(activity.action)}
                        {activity.action === 'invoice_cancelled'
                          && (activity.details?.auto || activity.details?.remote_id) && (
                          <span
                            title="Automatically cancelled — the balance was paid in your PMS (office payment)."
                            style={{
                              fontSize: '10px', fontWeight: 700, color: '#1d6b40',
                              backgroundColor: '#e7f4ec', border: '1px solid #c6e3d0',
                              borderRadius: '4px', padding: '1px 6px',
                            }}
                          >
                            AUTO
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b716d', marginTop: '2px' }}>
                        {formatTimestampInTimezone(activity.timestamp)}
                      </div>
                      {activity.details && Object.keys(activity.details).length > 0 && (
                        <div style={{ fontSize: '12px', color: '#8a908c', marginTop: '4px' }}>
                          {activity.details.phoneNumber && <>To: <span data-phi>{activity.details.phoneNumber}</span></>}
                          {activity.details.email && <>To: <span data-phi>{activity.details.email}</span></>}
                          {activity.details.replacedBy && (
                            <>Replaced by <span style={{ fontFamily: 'monospace' }}>{activity.details.replacedBy}</span></>
                          )}
                          {Array.isArray(activity.details.superseded) && activity.details.superseded.length > 0 && (
                            <>Superseded {activity.details.superseded.length} invoice
                            {activity.details.superseded.length === 1 ? '' : 's'}</>
                          )}
                          {activity.action === 'invoice_cancelled' && activity.details.reason && (
                            <div>Reason: {activity.details.reason}</div>
                          )}
                          {activity.action === 'invoice_cancelled' && activity.details.remote_id && (
                            <div>Matched PMS transaction {activity.details.remote_id}</div>
                          )}
                          {activity.action === 'payment_failed' && (
                            <>
                              {activity.details.refusalDescription || activity.details.refusalCode || 'Declined'}
                              {(activity.details.cardBrand || activity.details.cardLast4) && (
                                <> · {activity.details.cardBrand}{activity.details.cardLast4 ? ` ••${activity.details.cardLast4}` : ''}</>
                              )}
                            </>
                          )}
                          {activity.action === 'ledger_posted' && (
                            <>
                              Posted by Newton
                              {(timelineInvoice.reconciliationMatch?.remote_id || activity.details.zatannaPaymentId)
                                ? <> · txn {timelineInvoice.reconciliationMatch?.remote_id || activity.details.zatannaPaymentId}</>
                                : null}
                              {activity.details.reused ? ' · (idempotent replay)' : ''}
                            </>
                          )}
                          {activity.action === 'ledger_post_failed' && (
                            <span style={{ color: '#b42318' }}>
                              {activity.details.error || 'Could not post to the PMS ledger'}
                            </span>
                          )}
                          {activity.action === 'pms_reconciled' && (
                            <>
                              {activity.details.postedByNewton
                                ? 'Confirmed in PMS (posted by Newton)'
                                : `Matched PMS transaction${activity.details.remote_id ? ` ${activity.details.remote_id}` : ''}`}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );});
              })()}
            </div>

            {/* Footer actions. The "Cancel Invoice" button is the only
                way for staff to manually void a pending invoice — it
                fires POST /api/payments/invoices/<id>/cancel, which
                flips status to 'cancelled', stamps cancelledAt /
                cancelledBy, and removes the row from the Pending
                Payments tab. We gate it on status==='pending' so we
                never offer to cancel a paid/processing/superseded/
                already-cancelled invoice (those need refunds or have
                their lifecycle owned elsewhere). Confirmation uses
                window.confirm to keep the destructive action a
                deliberate two-step. */}
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <div>
                {timelineInvoice.status?.toLowerCase() === 'pending' && (
                  <button
                    onClick={async () => {
                      const confirmed = window.confirm(
                        'Cancel this invoice?\n\n'
                        + 'It will be removed from Pending Payments and the patient will no longer be able to pay it. '
                        + 'The invoice stays in the Cancelled tab for audit. This cannot be undone.'
                      );
                      if (!confirmed) return;
                      const invoiceIdToCancel = timelineInvoice.id;
                      setIsCancellingInvoice(true);
                      try {
                        const result = await cancelInvoice(practiceId, invoiceIdToCancel);
                        if (result && result.success) {
                          showToast('Invoice cancelled');
                          // Optimistically merge the server's authoritative
                          // copy into our local list so the row disappears
                          // from Pending and shows up under Cancelled
                          // immediately — saves a round trip to loadInvoices.
                          if (result.invoice) {
                            setInvoices(prev => prev.map(inv =>
                              inv.id === invoiceIdToCancel ? { ...inv, ...result.invoice } : inv
                            ));
                          }
                          setShowTimelineModal(false);
                          setTimelineInvoice(null);
                        } else {
                          showToast(result?.message || result?.error || 'Failed to cancel invoice', 'error');
                        }
                      } catch (e) {
                        console.error('Error cancelling invoice', e);
                        showToast('Error cancelling invoice', 'error');
                      } finally {
                        setIsCancellingInvoice(false);
                      }
                    }}
                    disabled={isCancellingInvoice}
                    style={{
                      backgroundColor: isCancellingInvoice ? '#f5c6c1' : '#b42318',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      cursor: isCancellingInvoice ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    title="Void this invoice. It will be removed from Pending Payments."
                  >
                    {isCancellingInvoice ? 'Cancelling…' : '⊘ Cancel Invoice'}
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setShowTimelineModal(false);
                  setTimelineInvoice(null);
                }}
                style={{
                  backgroundColor: '#6b716d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: toast.type === 'success' ? '#1d6b40' : '#b42318',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontSize: '14px',
          fontWeight: '500',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {toast.type === 'success' ? '✓' : '✕'} {toast.message}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default InvoiceTab;
